var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState, useCallback, useMemo, useReducer, useContext, } from 'react';
import customEventHandler from '../helper/customEvents';
import globalStore from '../store';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import { bringManipulativesToFront, reorderChainByIndex } from '../helper/helper';
import { playAudio } from '../helper/audioHelper';
import { v4 as uuidv4 } from 'uuid';
import '../styles/widgets/linkingCubes.scss';
import { DataContext } from '../player/DataContext';
import { ColorEnum } from '../components/ColorPicker';
import { announce, announceAssertive } from '../helper/announcer';
import { t } from 'i18next';
import { GRID_SIZE } from '../constants/gridConstants';
import SelectFocusOverlay from '../components/SelectFocusOverlay';
import { useMultiSelectDragSource } from '../helper/hooks/useMultiSelectDrag';
// Reliable touch-primary detection — works on modern iPads (desktop UA)
var IS_TOUCH_DEVICE = typeof window !== 'undefined' && !!((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(pointer: coarse)').matches);
// Reverse map: hex color → index for SVG filename. Must match the physical
// cubeN_<idx>.svg files shipped under assets/images/manipulative/linkingCube/:
// idx 3 = green, 4 = purple, 5 = yellow, 6 = orange (same mapping as the
// toolbar's CUBE_COLOR_INDEX).
var COLOR_INDEX_MAP = {
    RED: 0,
    BLUE: 1,
    BROWN: 2,
    GREEN: 3,
    PURPLE: 4,
    YELLOW: 5,
    ORANGE: 6,
    BLACK: 7,
    WHITE: 8,
    GRAY: 9,
    DARK_NAVY_BLUE: 10,
};
var COLOR_HEX_TO_INDEX = Object.fromEntries(Object.entries(COLOR_INDEX_MAP).map(function (_a) {
    var key = _a[0], idx = _a[1];
    return [
        ColorEnum[key],
        idx,
    ];
}));
// Reverse map: hex color → readable name for accessibility
export var COLOR_HEX_TO_NAME = Object.fromEntries(Object.entries(COLOR_INDEX_MAP).map(function (_a) {
    var key = _a[0];
    return [
        ColorEnum[key],
        key.toLowerCase().replace(/_/g, ' '),
    ];
}));
var getCubeImgSrc = function (orientation, hexColor) {
    var _a;
    var idx = (_a = COLOR_HEX_TO_INDEX[hexColor]) !== null && _a !== void 0 ? _a : 7; // fallback to BLACK
    return "./assets/images/manipulative/linkingCube/".concat(orientation, "/cubeN_").concat(idx, ".svg");
};
// Cube positioning step is locked to the background grid cell size so each
// cube center sits on a grid line and N linked cubes span ~N grid squares.
export var CUBE_SIZE = GRID_SIZE;
/** Actual CSS width/height of the .linkingCube button element. Larger than
 * CUBE_SIZE by design — the 10px difference is the interlock overlap between
 * adjacent linked cubes. */
export var CSS_CUBE_SIZE = 65;
export var UNLINK_SPACING = 10;
export var SNAP_THRESHOLD = 20;
export var SNAP_ALIGN_THRESHOLD = 15;
var SCALE_STEP = 0.1;
var _linkPickerState = null;
var _linkPickerSubscribers = new Set();
var getLinkPickerState = function () { return _linkPickerState; };
var setLinkPickerState = function (next) {
    _linkPickerState = next;
    _linkPickerSubscribers.forEach(function (fn) { return fn(); });
};
var useLinkPickerState = function () {
    var _a = useReducer(function (x) { return x + 1; }, 0), force = _a[1];
    useEffect(function () {
        _linkPickerSubscribers.add(force);
        return function () {
            _linkPickerSubscribers.delete(force);
        };
    }, []);
    return _linkPickerState;
};
var LinkingCubes = function (_a) {
    var id = _a.id, data = _a.data, calledFrom = _a.calledFrom, inChain = _a.inChain;
    var color = data.color, center = data.center, orientation = data.orientation, _b = data.scale, scale = _b === void 0 ? 1 : _b, _d = data.rotation, rotation = _d === void 0 ? 0 : _d, lock = data.lock, chainId = data.chainId, chainIndex = data.chainIndex;
    var _e = useContext(DataContext), setManipulative = _e.setManipulative, selectedTool = _e.selectedTool, setSelectedTool = _e.setSelectedTool, manipulative = _e.manipulative, panningEnable = _e.panningEnable, setContextMenu = _e.setContextMenu;
    // Broadcasts this cube/chain's drag as a multi-select leader so OTHER-type items (dice,
    // tiles, decks, ...) selected alongside it also follow. This widget's own same-type
    // multi-select logic below (in the mousedown handler's onMove/onUp) already coordinates
    // OTHER linkingCubes items directly — it predates and doesn't use this hook.
    var _f = useMultiSelectDragSource(id, selectedTool), startMultiDrag = _f.startMultiDrag, moveMultiDrag = _f.moveMultiDrag, endMultiDrag = _f.endMultiDrag;
    var zoomLevel = globalStore.useStoreState(function (store) { return store.playerStore; }).zoomLevel;
    var cubeRef = useRef(null);
    var isDraggable = !lock.positioning;
    var isSelected = selectedTool.includes(id);
    // ─── Keyboard link-picker subscription ─────────────────────────────────
    var linkPicker = useLinkPickerState();
    var isLinkPickerSource = (linkPicker === null || linkPicker === void 0 ? void 0 : linkPicker.sourceId) === id;
    var isLinkCandidate = !!linkPicker && linkPicker.candidateIds.includes(id);
    var isCurrentLinkCandidate = isLinkCandidate &&
        linkPicker.candidateIds[linkPicker.currentIndex] === id;
    // ─── Keyboard accessibility state ─────────────────────────────────────
    var keyMoveStartRef = useRef(null);
    var keyMoveCurPosRef = useRef(null);
    var keyMoveSaveTimer = useRef(null);
    var _g = useState(''), announceText = _g[0], setAnnounceText = _g[1];
    var _h = useState(false), isFocused = _h[0], setIsFocused = _h[1];
    // Transient CSS-only bring-to-front while actively dragging — mirrors
    // NumberFrames' `dragStart` boost. The permanent stacking commit happens
    // via array reorder on drop, not via zIndex. Broadcast via customEventHandler
    // (rather than local state) because a single drag session moves every cube
    // in the grabbed chain, not just the one that received the mousedown.
    var _j = useState([]), activeDragIds = _j[0], setActiveDragIds = _j[1];
    var isDragging = activeDragIds.includes(id);
    useEffect(function () {
        var onDragActive = function (ids) { return setActiveDragIds(ids); };
        customEventHandler.addEventListener('linkingCubeDragActive', onDragActive);
        return function () {
            customEventHandler.removeEventListener('linkingCubeDragActive', onDragActive);
        };
    }, []);
    // Mid-chain cubes (chainIndex > 0) are like dice-pool members — not tab-focusable,
    // no arrow-key movement. Base (chainIndex === 0/undefined) and standalone cubes are.
    var isInChainMember = !!chainId && (chainIndex !== null && chainIndex !== void 0 ? chainIndex : 0) > 0;
    var isBaseOrStandalone = !isInChainMember;
    // Every selected cube renders its own drop-shadow halo, including those in
    // a fully-selected chain (no separate rectangular overlay).
    var showIndividualSelection = isSelected;
    // ─── Chain-wide lock detection ───────────────────────────────────────
    // Mirrors handleMouseDown drag subset: base cube (index 0) drags the entire
    // chain so check all cubes; non-base only drags its tail (index >= own).
    var isChainLocked = useMemo(function () {
        if (!chainId)
            return lock.positioning;
        var isBase = chainIndex === 0 || chainIndex === undefined;
        return manipulative.some(function (m) {
            if (m.data.type !== 'linkingCubes')
                return false;
            var c = m.data;
            if (c.chainId !== chainId)
                return false;
            if (!isBase && c.chainIndex !== undefined && c.chainIndex < chainIndex)
                return false;
            return c.lock.positioning;
        });
    }, [chainId, chainIndex, lock.positioning, manipulative]);
    // ─── Bottom panel events (scale) ───────────────────────────────────
    // Only the first selected cube handles the event to avoid race conditions
    // when multiple cubes each register their own listener.
    useEffect(function () {
        var onToolClick = function (evtData) {
            if (evtData.id !== 'scaleMinus' && evtData.id !== 'scalePlus')
                return;
            // Guard: only the first selected cube processes the scale event
            if (selectedTool[0] !== id)
                return;
            var isPlus = evtData.id === 'scalePlus';
            // Group selected cubes by chainId (undefined key = standalone)
            var chainGroups = new Map();
            var _loop_1 = function (toolId) {
                var cube = manipulative.find(function (m) { return m.data.type === 'linkingCubes' && m.data.id === toolId; });
                if (!cube)
                    return "continue";
                var cd = cube.data;
                var key = cd.chainId;
                if (!chainGroups.has(key))
                    chainGroups.set(key, []);
                chainGroups.get(key).push(cd);
            };
            for (var _a = 0, selectedTool_1 = selectedTool; _a < selectedTool_1.length; _a++) {
                var toolId = selectedTool_1[_a];
                _loop_1(toolId);
            }
            // Build update map: cubeId → { scale, center }
            var cubeUpdates = new Map();
            chainGroups.forEach(function (cubes, chainKey) {
                var _a, _b;
                var representative = cubes[0];
                var oldScale = (_a = representative.scale) !== null && _a !== void 0 ? _a : 1;
                var newScale = isPlus ? oldScale + SCALE_STEP : Math.max(0.5, oldScale - SCALE_STEP);
                if (newScale === oldScale)
                    return;
                var newScaledSize = CUBE_SIZE * newScale;
                if (chainKey) {
                    // Full chain — include ALL members, reposition from base
                    var chainCubes = manipulative
                        .filter(function (m) { return m.data.type === 'linkingCubes' && m.data.chainId === chainKey; })
                        .map(function (m) { return m.data; });
                    var baseCube = chainCubes.find(function (c) { var _a; return ((_a = c.chainIndex) !== null && _a !== void 0 ? _a : 0) === 0; }) || chainCubes[0];
                    var baseCenter = baseCube.center;
                    for (var _d = 0, chainCubes_1 = chainCubes; _d < chainCubes_1.length; _d++) {
                        var cc = chainCubes_1[_d];
                        var idx = (_b = cc.chainIndex) !== null && _b !== void 0 ? _b : 0;
                        var newCenter = cc.orientation === 'horizontal'
                            ? { x: baseCenter.x + idx * newScaledSize, y: baseCenter.y }
                            : { x: baseCenter.x, y: baseCenter.y - idx * newScaledSize };
                        cubeUpdates.set(cc.id, { scale: newScale, center: newCenter });
                    }
                }
                else {
                    // Standalone cubes — just update scale, keep center
                    for (var _e = 0, cubes_1 = cubes; _e < cubes_1.length; _e++) {
                        var cc = cubes_1[_e];
                        cubeUpdates.set(cc.id, { scale: newScale, center: cc.center });
                    }
                }
            });
            if (cubeUpdates.size === 0)
                return;
            // Collect old and new state for only the affected items
            var oldItems = [];
            var newItems = [];
            var updated = manipulative.map(function (item) {
                var upd = item.data.id ? cubeUpdates.get(item.data.id) : undefined;
                if (!upd)
                    return item;
                oldItems.push(item);
                var newItem = __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { scale: upd.scale, center: upd.center }) });
                newItems.push(newItem);
                return newItem;
            });
            saveState({
                type: 'multipleManipulatives',
                id: id,
                propertyName: 'multipleCompleteData',
                old: JSON.stringify(oldItems),
                new: JSON.stringify(newItems),
            });
            setManipulative(function () { return updated; });
            // Announce the new scale value to screen readers. Use the first
            // affected cube's resulting scale as the representative value (all
            // cubes within a chain share the same scale).
            var firstUpdate = cubeUpdates.values().next().value;
            if (firstUpdate) {
                var scalePercent = Math.round(firstUpdate.scale * 100);
                announceAssertive(t('text.linkingCubeScaled', {
                    direction: isPlus ? t('text.up') : t('text.down'),
                    value: scalePercent,
                }));
            }
        };
        if (isSelected)
            customEventHandler.addEventListener('bottomPanel', onToolClick);
        return function () {
            customEventHandler.removeEventListener('bottomPanel', onToolClick);
        };
    }, [isSelected, manipulative, id, selectedTool, setManipulative]);
    // ─── Refs that keep latest values accessible inside window listeners ───
    var manipulativeRef = useRef(manipulative);
    manipulativeRef.current = manipulative;
    var zoomRef = useRef(zoomLevel);
    zoomRef.current = zoomLevel;
    var selectedToolRef = useRef(selectedTool);
    selectedToolRef.current = selectedTool;
    var centerRef = useRef(center);
    centerRef.current = center;
    // Follow a DIFFERENT-type leader (e.g. Dice, a card deck) dragging alongside this cube in
    // the current multi-select. Following ANOTHER linkingCubes leader is already handled by
    // this file's own drag.startPositions system in the mousedown handler's onMove — this
    // effect only needs to fire for a source of some other type (linkingCubes is excluded
    // from the central engine's own follower handling; see SELF_MANAGED_TYPES in
    // multiDragHelper.ts).
    useEffect(function () {
        var isMultiSelected = selectedTool.includes(id) && selectedTool.length > 1;
        if (!isMultiSelected)
            return;
        var myStart = { x: centerRef.current.x, y: centerRef.current.y };
        var lastAppliedCenter = null;
        var onStart = function (_a) {
            var sourceId = _a.sourceId;
            if (sourceId === id)
                return;
            myStart = { x: centerRef.current.x, y: centerRef.current.y };
            lastAppliedCenter = null;
        };
        var onMove = function (_a) {
            var sourceId = _a.sourceId, deltaX = _a.deltaX, deltaY = _a.deltaY;
            if (sourceId === id || (lock === null || lock === void 0 ? void 0 : lock.positioning))
                return;
            var inMultiSel = selectedToolRef.current.includes(id) && selectedToolRef.current.length > 1;
            if (!inMultiSel)
                return;
            // A linkingCube leader's drag is already fully handled by this file's own
            // drag.startPositions system in handleMouseDown — this effect only exists
            // to follow OTHER-type leaders. Without this guard, a mid-chain tear-off
            // (which deliberately keeps head cubes in the selection but excludes them
            // from drag.startPositions) would have those head cubes follow the tail's
            // broadcast delta here, visually reattaching the chain. Mirrors the same
            // check in onEnd below.
            var sourceItem = manipulativeRef.current.find(function (m) { return m.data.id === sourceId; });
            if ((sourceItem === null || sourceItem === void 0 ? void 0 : sourceItem.data.type) === 'linkingCubes')
                return;
            var newCenter = { x: myStart.x + deltaX, y: myStart.y + deltaY };
            lastAppliedCenter = newCenter;
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === id
                        ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: newCenter }) }) : item;
                });
            });
        };
        var onEnd = function (_a) {
            var sourceId = _a.sourceId;
            if (sourceId === id)
                return;
            if (!lastAppliedCenter)
                return;
            // A linkingCube leader already batch-commits every follower's final center in its
            // own drag.startPositions system — only self-save when the leader is some OTHER type
            // (Dice, a card deck, Shades, ...), same guard ColoredTiles.tsx uses.
            var sourceItem = manipulativeRef.current.find(function (m) { return m.data.id === sourceId; });
            if ((sourceItem === null || sourceItem === void 0 ? void 0 : sourceItem.data.type) === 'linkingCubes') {
                lastAppliedCenter = null;
                return;
            }
            var oldCenter = myStart;
            var newCenter = lastAppliedCenter;
            lastAppliedCenter = null;
            var entry = {
                type: id,
                id: id.split('_')[1],
                propertyName: 'center',
                old: JSON.stringify(oldCenter),
                new: JSON.stringify(newCenter),
            };
            if (!registerMultiSelectSaveEntries(sourceId, [entry])) {
                saveState(entry);
            }
        };
        customEventHandler.addEventListener('multiSelectMoveStart', onStart);
        customEventHandler.addEventListener('multiSelectMove', onMove);
        customEventHandler.addEventListener('multiSelectMoveEnd', onEnd);
        return function () {
            customEventHandler.removeEventListener('multiSelectMoveStart', onStart);
            customEventHandler.removeEventListener('multiSelectMove', onMove);
            customEventHandler.removeEventListener('multiSelectMoveEnd', onEnd);
        };
    }, [selectedTool, id, lock === null || lock === void 0 ? void 0 : lock.positioning, setManipulative]);
    // The browser fires a native `click` after `mouseup` regardless of whether a drag
    // happened in between — handleClick's "plain click" branches would otherwise replace
    // the whole selection with just this cube/chain right after a real drag, wiping out
    // any other selected item that was following along. Set in onUp when a real drag
    // occurred; checked (and cleared) at the top of handleClick.
    var justDraggedRef = useRef(false);
    var dragRef = useRef(null);
    // ─── Helpers (use refs, NOT state, so they never go stale) ────────────
    var getChainCubesFromRef = useCallback(function (cId) {
        var targetChainId = cId !== null && cId !== void 0 ? cId : chainId;
        if (!targetChainId)
            return [data];
        return manipulativeRef.current
            .filter(function (m) { return m.data.type === 'linkingCubes' && m.data.chainId === targetChainId; })
            .sort(function (a, b) { var _a, _b; return ((_a = a.data.chainIndex) !== null && _a !== void 0 ? _a : 0) - ((_b = b.data.chainIndex) !== null && _b !== void 0 ? _b : 0); })
            .map(function (m) { return m.data; });
    }, [chainId, data]);
    var detectSnap = function (cubeCenter, cubeOrientation, excludeIds, dragScale) {
        if (dragScale === void 0) { dragScale = scale; }
        return detectSnapTarget(cubeCenter, cubeOrientation, excludeIds, manipulativeRef.current, dragScale);
    };
    var linkToTarget = function (snapResult, draggedIds, preDragItems) {
        var _a, _b;
        var targetCube = snapResult.targetCube, snappedPosition = snapResult.snappedPosition, side = snapResult.side;
        var items = manipulativeRef.current;
        var targetScale = (_a = targetCube.scale) !== null && _a !== void 0 ? _a : 1;
        var scaledSize = CUBE_SIZE * targetScale;
        var targetChainId = targetCube.chainId || "chain_".concat(uuidv4().replace(/-/g, ''));
        var targetChainCubes = targetCube.chainId
            ? items
                .filter(function (m) {
                return m.data.type === 'linkingCubes' &&
                    m.data.chainId === targetCube.chainId;
            })
                .map(function (m) { return m.data; })
                .sort(function (a, b) { var _a, _b; return ((_a = a.chainIndex) !== null && _a !== void 0 ? _a : 0) - ((_b = b.chainIndex) !== null && _b !== void 0 ? _b : 0); })
            : [targetCube];
        var draggedCubes = items
            .filter(function (m) { return m.data.id && draggedIds.includes(m.data.id); })
            .map(function (m) { return m.data; })
            .sort(function (a, b) { var _a, _b; return ((_a = a.chainIndex) !== null && _a !== void 0 ? _a : 0) - ((_b = b.chainIndex) !== null && _b !== void 0 ? _b : 0); });
        // Affected ids for undo: dragged cubes plus every existing target-chain member,
        // since inserting 'before' shifts their chainIndex too.
        var affectedIds = new Set(__spreadArray(__spreadArray([], draggedIds, true), targetChainCubes.map(function (c) { return c.id; }).filter(function (cid) { return !!cid; }), true));
        // `items` (manipulativeRef.current) is read at CALL time — after onMove has
        // already committed the drag's movement — so for dragged cubes it only ever
        // reflects their POST-drag position, never their true pre-drag one. Use the
        // pre-drag snapshot (captured at mousedown, before any movement) for those;
        // non-dragged pre-existing target-chain members never moved, so the live
        // lookup is already correct for them.
        var oldItems = items
            .filter(function (m) { return m.data.id && affectedIds.has(m.data.id); })
            .map(function (m) {
            return m.data.id && draggedIds.includes(m.data.id) && (preDragItems === null || preDragItems === void 0 ? void 0 : preDragItems.has(m.data.id))
                ? preDragItems.get(m.data.id)
                : m;
        });
        var buildUpdated = function () {
            var updated = items.map(function (item) {
                var _a, _b;
                var cubeData = item.data;
                // Assign chainId to target if it had none
                if (item.data.id === targetCube.id && !targetCube.chainId) {
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { chainId: targetChainId, chainIndex: side === 'after' ? 0 : draggedCubes.length }) });
                }
                // Shift target chain indices when inserting before (skip dragged cubes)
                if (side === 'before' &&
                    targetCube.chainId &&
                    cubeData.chainId === targetCube.chainId &&
                    !draggedIds.includes((_a = item.data.id) !== null && _a !== void 0 ? _a : '')) {
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { chainId: targetChainId, chainIndex: ((_b = cubeData.chainIndex) !== null && _b !== void 0 ? _b : 0) + draggedCubes.length }) });
                }
                // Position dragged cubes
                var draggedIndex = draggedCubes.findIndex(function (d) { return d.id === item.data.id; });
                if (draggedIndex !== -1) {
                    var baseIndex = side === 'after' ? targetChainCubes.length : 0;
                    var newCenter = void 0;
                    if (side === 'before') {
                        // snappedPosition = slot closest to target; extend away from target
                        var distFromEnd = draggedCubes.length - 1 - draggedIndex;
                        newCenter =
                            orientation === 'horizontal'
                                ? { x: snappedPosition.x - distFromEnd * scaledSize, y: snappedPosition.y }
                                : { x: snappedPosition.x, y: snappedPosition.y + distFromEnd * scaledSize };
                    }
                    else {
                        newCenter =
                            orientation === 'horizontal'
                                ? { x: snappedPosition.x + draggedIndex * scaledSize, y: snappedPosition.y }
                                : { x: snappedPosition.x, y: snappedPosition.y - draggedIndex * scaledSize };
                    }
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { chainId: targetChainId, chainIndex: baseIndex + draggedIndex, center: newCenter, scale: targetScale }) });
                }
                return item;
            });
            // Reorder chain members ascending by chainIndex so array position
            // matches the intended stacking (highest chainIndex = frontmost).
            return reorderChainByIndex(updated, targetChainId);
        };
        var reordered = buildUpdated();
        setManipulative(function () { return reordered; });
        var newItems = reordered.filter(function (m) { return m.data.id && affectedIds.has(m.data.id); });
        if (oldItems.length > 0) {
            var entry = {
                type: 'multipleManipulatives',
                id: Array.from(affectedIds).join(','),
                propertyName: 'multipleCompleteData',
                old: JSON.stringify(oldItems),
                new: JSON.stringify(newItems),
            };
            var accepted = registerMultiSelectSaveEntries(id, [entry]);
            if (!accepted) {
                saveState([entry]);
            }
        }
        // Announce the link for screen readers
        var finalChainSize = targetChainCubes.length + draggedCubes.length;
        var linkedColorName = (_b = COLOR_HEX_TO_NAME[targetCube.color]) !== null && _b !== void 0 ? _b : 'cube';
        announce(t('text.linkingCubeLinked', { color: linkedColorName, count: finalChainSize }));
    };
    var breakChainAt = function (currentChainId, breakIndex) {
        var _a;
        var newChainId = "chain_".concat(uuidv4().replace(/-/g, ''));
        // Count members on each side for announcement
        var beforeMembers = manipulativeRef.current.filter(function (m) {
            return m.data.type === 'linkingCubes' &&
                m.data.chainId === currentChainId;
        });
        var leftCount = beforeMembers.filter(function (m) { var _a; return ((_a = m.data.chainIndex) !== null && _a !== void 0 ? _a : 0) < breakIndex; }).length;
        var rightCount = beforeMembers.length - leftCount;
        var brokenColor = beforeMembers.length > 0
            ? (_a = COLOR_HEX_TO_NAME[beforeMembers[0].data.color]) !== null && _a !== void 0 ? _a : 'cube'
            : 'cube';
        // If either side ends up with a single cube, that cube isn't a chain
        // anymore — clear its chainId/chainIndex so it reads as standalone.
        // Without this, splitting a 2-cube chain leaves both sides with a
        // chainId, and the context menu's "if (focusedChainId)" check keeps
        // showing "Unlink Cubes" / "Unlink All Cubes" on what's now a lone cube.
        var leftSideSingleton = leftCount === 1;
        var rightSideSingleton = rightCount === 1;
        setManipulative(function (prev) {
            return prev.map(function (item) {
                var _a, _b;
                if (item.data.type !== 'linkingCubes')
                    return item;
                var cubeData = item.data;
                if (cubeData.chainId !== currentChainId)
                    return item;
                var goesToNewChain = ((_a = cubeData.chainIndex) !== null && _a !== void 0 ? _a : 0) >= breakIndex;
                if (goesToNewChain) {
                    if (rightSideSingleton) {
                        return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { chainId: undefined, chainIndex: undefined }) });
                    }
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { chainId: newChainId, chainIndex: ((_b = cubeData.chainIndex) !== null && _b !== void 0 ? _b : 0) - breakIndex }) });
                }
                // Stays on original chain — but if it's now alone, dissolve it.
                if (leftSideSingleton) {
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { chainId: undefined, chainIndex: undefined }) });
                }
                return item;
            });
        });
        announce(t('text.chainBroken', {
            left: leftCount,
            right: rightCount,
            color: brokenColor,
        }));
        return newChainId;
    };
    // ─── Context-menu "Unlink all" action ─────────────────────────────────
    // Dispatched by ContextMenu.tsx on a chain base. Dissolves the whole chain:
    // every member loses its chainId / chainIndex and stays at its current
    // position. Only the chain base responds so the action runs exactly once.
    useEffect(function () {
        if (calledFrom !== 'widget')
            return;
        var onDissolve = function (data) {
            var _a;
            if (!chainId || chainId !== data.chainId)
                return;
            if ((chainIndex !== null && chainIndex !== void 0 ? chainIndex : 0) !== 0)
                return;
            var snapshot = manipulativeRef.current
                .filter(function (m) {
                return m.data.type === 'linkingCubes' &&
                    m.data.chainId === chainId;
            })
                .map(function (m) { return JSON.parse(JSON.stringify(m)); });
            var count = snapshot.length;
            var color = count > 0
                ? (_a = COLOR_HEX_TO_NAME[snapshot[0].data.color]) !== null && _a !== void 0 ? _a : 'cube'
                : 'cube';
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    if (item.data.type !== 'linkingCubes')
                        return item;
                    var d = item.data;
                    if (d.chainId !== chainId)
                        return item;
                    var _c = d.chainId, _i = d.chainIndex, rest = __rest(d, ["chainId", "chainIndex"]);
                    return __assign(__assign({}, item), { data: rest });
                });
            });
            saveState({
                type: 'multipleManipulatives',
                id: id,
                propertyName: 'dissolveChain',
                old: JSON.stringify(snapshot),
            });
            playAudio('cubeUnlink');
            announce(t('text.linkingCubeChainDissolved', { count: count, color: color }));
            requestAnimationFrame(function () { var _a; return (_a = cubeRef.current) === null || _a === void 0 ? void 0 : _a.focus(); });
        };
        customEventHandler.addEventListener('linkingCubeDissolveChain', onDissolve);
        return function () {
            return customEventHandler.removeEventListener('linkingCubeDissolveChain', onDissolve);
        };
    }, [calledFrom, id, chainId, chainIndex, setManipulative]);
    // ─── Context-menu "Link with another cube" action ─────────────────────
    // Listens for linkingCubeStartLink; builds candidate list (same orientation,
    // not in source's own chain) and enters picker mode. Only chain-base cubes
    // and standalone cubes respond. Focus moves to the first candidate's base.
    useEffect(function () {
        if (calledFrom !== 'widget')
            return;
        var onStartLink = function (data) {
            if (data.id !== id)
                return;
            // Only standalone cubes can initiate Link. Chains cannot be sources —
            // the user must Unlink all first if they want to relink chain members.
            if (chainId)
                return;
            var sourceChainId = chainId;
            var candidateBases = manipulativeRef.current
                .filter(function (m) { return m.data.type === 'linkingCubes'; })
                .map(function (m) { return m.data; })
                .filter(function (c) {
                var _a;
                if (c.id === id)
                    return false;
                if (c.orientation !== orientation)
                    return false;
                // Skip any cube in the source's own chain.
                if (sourceChainId && c.chainId === sourceChainId)
                    return false;
                // Candidates are bases of chains or standalone cubes — never mid-chain.
                if (c.chainId && ((_a = c.chainIndex) !== null && _a !== void 0 ? _a : 0) !== 0)
                    return false;
                return true;
            });
            if (candidateBases.length === 0) {
                announce(t('text.linkingCubeLinkPickerNone'));
                return;
            }
            setLinkPickerState({
                sourceId: id,
                candidateIds: candidateBases.map(function (c) { return c.id; }),
                currentIndex: 0,
                mode: 'link',
            });
            announce(t('text.linkingCubeLinkPickerDummyStart'));
            // Focus the first candidate's dummy overlay.
            var focusFirstDummy = function (attempts) {
                if (attempts === void 0) { attempts = 0; }
                var el = document.querySelector("[data-link-dummy=\"".concat(candidateBases[0].id, "\"]"));
                if (el) {
                    el.focus();
                }
                else if (attempts < 5) {
                    setTimeout(function () { return focusFirstDummy(attempts + 1); }, 50);
                }
            };
            setTimeout(function () { return focusFirstDummy(); }, 50);
        };
        customEventHandler.addEventListener('linkingCubeStartLink', onStartLink);
        return function () {
            return customEventHandler.removeEventListener('linkingCubeStartLink', onStartLink);
        };
    }, [calledFrom, id, chainId, chainIndex, orientation]);
    // ─── Context-menu "Unlink Cubes" (split) action ─────────────────────────
    // Listens for linkingCubeStartUnlink; only the chain base responds.
    // Builds candidates = chain members with chainIndex >= 1 (skip the base —
    // breaking at index 0 is identical to "Unlink All Cubes" and would be a
    // duplicate option). Selecting cube k mirrors the mouse drag-break:
    // breakChainAt(chainId, k) keeps cubes 0..k-1 on the original chain and
    // moves k..end to a new chain.
    useEffect(function () {
        if (calledFrom !== 'widget')
            return;
        var onStartUnlink = function (data) {
            var _a;
            if (data.id !== id)
                return;
            if (!chainId)
                return;
            if ((chainIndex !== null && chainIndex !== void 0 ? chainIndex : 0) !== 0)
                return;
            var chainMembers = manipulativeRef.current
                .filter(function (m) {
                return m.data.type === 'linkingCubes' &&
                    m.data.chainId === chainId;
            })
                .map(function (m) { return m.data; })
                .sort(function (a, b) { var _a, _b; return ((_a = a.chainIndex) !== null && _a !== void 0 ? _a : 0) - ((_b = b.chainIndex) !== null && _b !== void 0 ? _b : 0); });
            var candidates = chainMembers.filter(function (c) { var _a; return ((_a = c.chainIndex) !== null && _a !== void 0 ? _a : 0) >= 1; });
            if (candidates.length === 0) {
                announce(t('text.linkingCubeUnlinkPickerNone'));
                return;
            }
            setLinkPickerState({
                sourceId: id,
                candidateIds: candidates.map(function (c) { return c.id; }),
                currentIndex: 0,
                mode: 'unlink',
            });
            var baseColorName = (_a = COLOR_HEX_TO_NAME[color]) !== null && _a !== void 0 ? _a : 'cube';
            announce(t('text.linkingCubeUnlinkPickerStart', { color: baseColorName }));
            var focusFirstDummy = function (attempts) {
                if (attempts === void 0) { attempts = 0; }
                var el = document.querySelector("[data-link-dummy=\"".concat(candidates[0].id, "\"]"));
                if (el) {
                    el.focus();
                }
                else if (attempts < 5) {
                    setTimeout(function () { return focusFirstDummy(attempts + 1); }, 50);
                }
            };
            setTimeout(function () { return focusFirstDummy(); }, 50);
        };
        customEventHandler.addEventListener('linkingCubeStartUnlink', onStartUnlink);
        return function () {
            return customEventHandler.removeEventListener('linkingCubeStartUnlink', onStartUnlink);
        };
    }, [calledFrom, id, chainId, chainIndex, color]);
    // ─── Delete: remove this cube and renumber any surviving chain members ─
    var removeCubeAndRenumberChain = useCallback(function () {
        var before = manipulativeRef.current;
        var removedItem = before.find(function (m) { return m.data.id === id; });
        if (!removedItem)
            return;
        // Surviving members of this chain (if any), sorted by chainIndex
        var survivors = chainId
            ? before
                .filter(function (m) {
                return m.data.type === 'linkingCubes' &&
                    m.data.chainId === chainId &&
                    m.data.id !== id;
            })
                .sort(function (a, b) {
                var _a, _b;
                return ((_a = a.data.chainIndex) !== null && _a !== void 0 ? _a : 0) -
                    ((_b = b.data.chainIndex) !== null && _b !== void 0 ? _b : 0);
            })
            : [];
        var dissolveChain = survivors.length <= 1;
        var renumberMap = new Map();
        if (chainId) {
            if (dissolveChain && survivors[0]) {
                // Lone survivor: drop chainId/chainIndex entirely
                renumberMap.set(survivors[0].data.id, { chainId: undefined, chainIndex: undefined });
            }
            else {
                // Renumber 0..n-1 in sorted order
                survivors.forEach(function (m, idx) {
                    renumberMap.set(m.data.id, { chainId: chainId, chainIndex: idx });
                });
            }
        }
        var oldItems = [removedItem];
        var newItems = [];
        var after = [];
        for (var _a = 0, before_1 = before; _a < before_1.length; _a++) {
            var m = before_1[_a];
            if (m.data.id === id)
                continue; // drop
            var rn = m.data.id ? renumberMap.get(m.data.id) : undefined;
            if (rn) {
                oldItems.push(m);
                var next = __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { chainId: rn.chainId, chainIndex: rn.chainIndex }) });
                newItems.push(next);
                after.push(next);
            }
            else {
                after.push(m);
            }
        }
        saveState({
            type: 'multipleManipulatives',
            id: id,
            propertyName: 'multipleCompleteDataDelete',
            old: JSON.stringify(oldItems),
            new: JSON.stringify(newItems),
        });
        setManipulative(function () { return after; });
        setSelectedTool(selectedTool.filter(function (s) { return s !== id; }));
    }, [id, chainId, setManipulative, setSelectedTool, selectedTool]);
    // ─── Link-picker confirmation: reposition the source (cube or chain)
    // adjacent to the target chain end and merge into a single chain. Called
    // only from handleCubeKeyDown when this cube is the current picker candidate.
    var performLink = useCallback(function (sourceId, targetId, side) {
        var _a, _b, _d, _e, _f;
        var all = manipulativeRef.current;
        var source = (_a = all.find(function (m) { return m.data.id === sourceId; })) === null || _a === void 0 ? void 0 : _a.data;
        var target = (_b = all.find(function (m) { return m.data.id === targetId; })) === null || _b === void 0 ? void 0 : _b.data;
        if (!source || !target)
            return;
        var targetScale = (_d = target.scale) !== null && _d !== void 0 ? _d : 1;
        var scaledSize = CUBE_SIZE * targetScale;
        // Gather source's chain members (or just the source cube if standalone),
        // sorted ascending by chainIndex.
        var sourceChainMembers = (source.chainId
            ? all
                .filter(function (m) {
                return m.data.type === 'linkingCubes' &&
                    m.data.chainId === source.chainId;
            })
                .map(function (m) { return m.data; })
            : [source]).sort(function (a, b) { var _a, _b; return ((_a = a.chainIndex) !== null && _a !== void 0 ? _a : 0) - ((_b = b.chainIndex) !== null && _b !== void 0 ? _b : 0); });
        // Collect the target's chain members, sorted ascending by chainIndex.
        var targetChainMembers = (target.chainId
            ? all
                .filter(function (m) {
                return m.data.type === 'linkingCubes' &&
                    m.data.chainId === target.chainId;
            })
                .map(function (m) { return m.data; })
            : [target]).sort(function (a, b) { var _a, _b; return ((_a = a.chainIndex) !== null && _a !== void 0 ? _a : 0) - ((_b = b.chainIndex) !== null && _b !== void 0 ? _b : 0); });
        var finalChainId = (_e = target.chainId) !== null && _e !== void 0 ? _e : "chain_".concat(uuidv4().replace(/-/g, ''));
        var insertCount = sourceChainMembers.length;
        // Anchor: the cube at the target's selected end.
        var anchorCube = side === 'append'
            ? targetChainMembers[targetChainMembers.length - 1]
            : targetChainMembers[0];
        // Compute each source member's new center so the source block sits
        // adjacent to the target anchor, preserving the source's internal order.
        // For 'append' + vertical: source members stack above the anchor, with
        // source[0] (bottom of source chain) sitting just above the anchor.
        // For 'prepend' + vertical: source members stack below, source[last]
        // sitting just below the anchor.
        var newCenterForSource = function (sourceIdxInBlock) {
            // Offset from the anchor for source member at position sourceIdxInBlock
            // (0 = lowest chainIndex = visual bottom/left of source).
            if (target.orientation === 'vertical') {
                if (side === 'append') {
                    // Append: source block stacks above anchor. source[0] is new chainIndex
                    // = targetLen, source[1] is targetLen+1, etc.  Position: decreasing y.
                    return {
                        x: anchorCube.center.x,
                        y: anchorCube.center.y - scaledSize * (sourceIdxInBlock + 1),
                    };
                }
                else {
                    // Prepend: source block stacks below anchor. source[0] becomes
                    // new chainIndex 0 (new bottom). Position: increasing y for indices 0..
                    return {
                        x: anchorCube.center.x,
                        y: anchorCube.center.y + scaledSize * (insertCount - sourceIdxInBlock),
                    };
                }
            }
            if (side === 'append') {
                return {
                    x: anchorCube.center.x + scaledSize * (sourceIdxInBlock + 1),
                    y: anchorCube.center.y,
                };
            }
            return {
                x: anchorCube.center.x - scaledSize * (insertCount - sourceIdxInBlock),
                y: anchorCube.center.y,
            };
        };
        // Snapshot affected cubes for undo/redo.
        var beforeSnapshot = all
            .filter(function (m) {
            if (m.data.type !== 'linkingCubes')
                return false;
            var d = m.data;
            // Any member of source chain (or the source itself) + target chain.
            if (source.chainId && d.chainId === source.chainId)
                return true;
            if (!source.chainId && d.id === sourceId)
                return true;
            if (target.chainId && d.chainId === target.chainId)
                return true;
            if (!target.chainId && d.id === targetId)
                return true;
            return false;
        })
            .map(function (m) { return JSON.parse(JSON.stringify(m)); });
        // Index mapping: sourceId -> new chainIndex
        var sourceNewIndexMap = new Map();
        sourceChainMembers.forEach(function (c, i) {
            var newIdx = side === 'append' ? targetChainMembers.length + i : i;
            sourceNewIndexMap.set(c.id, newIdx);
        });
        // Index mapping: sourceId -> new center
        var sourceNewCenterMap = new Map();
        sourceChainMembers.forEach(function (c, i) {
            sourceNewCenterMap.set(c.id, newCenterForSource(i));
        });
        var beforeIds = all.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; });
        var updated = all.map(function (item) {
            var _a, _b, _d;
            if (item.data.type !== 'linkingCubes')
                return item;
            var d = item.data;
            // Source-chain members (or lone source) adopt target's chainId with
            // new positions and continued chainIndex.
            var isSourceMember = source.chainId
                ? d.chainId === source.chainId
                : d.id === sourceId;
            if (isSourceMember) {
                var newIdx = (_a = sourceNewIndexMap.get(d.id)) !== null && _a !== void 0 ? _a : 0;
                var newCenter = (_b = sourceNewCenterMap.get(d.id)) !== null && _b !== void 0 ? _b : d.center;
                return __assign(__assign({}, item), { data: __assign(__assign({}, d), { center: newCenter, position: newCenter, scale: targetScale, chainId: finalChainId, chainIndex: newIdx, orientation: target.orientation }) });
            }
            // Target chain members: assign chainId (if target was standalone)
            // and shift indices by insertCount if we prepended.
            var inTargetChain = target.chainId
                ? d.chainId === target.chainId
                : d.id === targetId;
            if (inTargetChain) {
                var currentIdx = (_d = d.chainIndex) !== null && _d !== void 0 ? _d : 0;
                var newIdx = side === 'prepend' ? currentIdx + insertCount : currentIdx;
                return __assign(__assign({}, item), { data: __assign(__assign({}, d), { chainId: finalChainId, chainIndex: newIdx }) });
            }
            return item;
        });
        // Reorder the merged chain ascending by chainIndex so array position
        // matches the intended stacking (highest chainIndex = frontmost).
        var reordered = reorderChainByIndex(updated, finalChainId);
        var afterIds = reordered.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; });
        setManipulative(function () { return reordered; });
        var batchEntries = [
            {
                type: 'multipleManipulatives',
                id: sourceId,
                propertyName: 'batchLink',
                old: JSON.stringify(beforeSnapshot),
            },
        ];
        if (beforeIds.join(',') !== afterIds.join(',')) {
            batchEntries.push({
                type: 'multipleManipulatives',
                id: sourceId,
                propertyName: 'reorder',
                old: JSON.stringify(beforeIds),
                new: JSON.stringify(afterIds),
            });
        }
        saveState(batchEntries);
        playAudio('cubeLink');
        var colorReadable = (_f = COLOR_HEX_TO_NAME[target.color]) !== null && _f !== void 0 ? _f : 'cube';
        if (insertCount > 1) {
            announce(t('text.linkingCubeChainMergedWithTarget', {
                sourceCount: insertCount,
                color: colorReadable,
                orientation: target.orientation,
            }));
        }
        else {
            announce(t('text.linkingCubeLinkedWithTarget', {
                color: colorReadable,
                orientation: target.orientation,
            }));
        }
        setLinkPickerState(null);
        // Focus returns to the new base of the merged chain so SR re-announces
        // the chain's full composition.
        requestAnimationFrame(function () {
            // The new base is whichever cube is at chainIndex 0 post-link.
            var newBaseIdx = side === 'prepend'
                ? sourceChainMembers[0].id
                : targetChainMembers[0].id;
            var el = document.querySelector("[data-id=\"".concat(newBaseIdx, "\"]"));
            el === null || el === void 0 ? void 0 : el.focus();
        });
    }, [setManipulative]);
    // ─── Keyboard handler (mirrors DiceManipulative onKeyDown pattern) ────
    var handleCubeKeyDown = useCallback(function (e) {
        var _a, _b, _d;
        if (calledFrom !== 'widget' || panningEnable)
            return;
        // Enter / Space / Shift+F10 / ContextMenu key — open the context menu.
        // Allowed for every chain member so keyboard users can target and unlink
        // individual cubes inside a chain. Selection mirrors the mouse path
        // (handleClick): a chained cube selects the WHOLE chain so the chain
        // selection overlay renders correctly; a standalone cube selects just
        // itself.
        if (e.key === 'Enter' ||
            e.key === ' ' ||
            e.key === 'ContextMenu' ||
            (e.shiftKey && e.key === 'F10')) {
            e.preventDefault();
            e.stopPropagation();
            if (chainId) {
                var chainIds = manipulativeRef.current
                    .filter(function (m) {
                    return m.data.type === 'linkingCubes' &&
                        m.data.chainId === chainId;
                })
                    .map(function (m) { return m.data.id; })
                    .filter(function (cid) { return !!cid; });
                setSelectedTool(chainIds);
            }
            else if (!selectedTool.includes(id)) {
                setSelectedTool([id]);
            }
            var rect = (_a = cubeRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (rect) {
                setContextMenu({
                    x: rect.right,
                    y: rect.top,
                    manipulativeId: id,
                });
            }
            return;
        }
        // Locked cubes can still receive focus and open the context menu (so
        // the user can unlock them), but cannot be moved or deleted via
        // keyboard shortcuts. Arrow keys are still swallowed below to prevent
        // page scroll on a locked focused cube.
        if (lock.editing) {
            if (e.key === 'ArrowUp' ||
                e.key === 'ArrowDown' ||
                e.key === 'ArrowLeft' ||
                e.key === 'ArrowRight') {
                e.preventDefault();
            }
            return;
        }
        // Delete / Backspace — remove this cube entirely
        if (e.key === 'Delete' || e.key === 'Backspace') {
            e.preventDefault();
            var chainLen = chainId
                ? manipulativeRef.current.filter(function (m) {
                    return m.data.type === 'linkingCubes' &&
                        m.data.chainId === chainId;
                }).length
                : 0;
            removeCubeAndRenumberChain();
            var colorReadable = (_b = COLOR_HEX_TO_NAME[color]) !== null && _b !== void 0 ? _b : 'cube';
            var chainInfo = chainId
                ? " ".concat(t('text.fromChainOf', { count: chainLen }))
                : '';
            announce(t('text.linkingCubeRemoved', { color: colorReadable, chainInfo: chainInfo }));
            return;
        }
        // Arrow keys — only base/standalone cubes move; skip when position-locked
        var isArrow = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key);
        if (!isArrow)
            return;
        if (!isBaseOrStandalone || lock.positioning) {
            // Still swallow to prevent page scroll
            e.preventDefault();
            return;
        }
        e.preventDefault();
        var step = (e.shiftKey ? 50 : 10) / (zoomRef.current || 1);
        var deltaMap = {
            ArrowUp: { x: 0, y: -step },
            ArrowDown: { x: 0, y: step },
            ArrowLeft: { x: -step, y: 0 },
            ArrowRight: { x: step, y: 0 },
        };
        var delta = deltaMap[e.key];
        var curPos = (_d = keyMoveCurPosRef.current) !== null && _d !== void 0 ? _d : center;
        if (!keyMoveStartRef.current)
            keyMoveStartRef.current = __assign({}, curPos);
        var newCenter = { x: curPos.x + delta.x, y: curPos.y + delta.y };
        keyMoveCurPosRef.current = newCenter;
        // If we're the base of a chain, translate every member by the same delta
        setManipulative(function (prev) {
            return prev.map(function (m) {
                if (m.data.type !== 'linkingCubes')
                    return m;
                var d = m.data;
                if (d.id === id) {
                    return __assign(__assign({}, m), { data: __assign(__assign({}, d), { center: newCenter }) });
                }
                if (chainId && d.chainId === chainId && d.id !== id) {
                    return __assign(__assign({}, m), { data: __assign(__assign({}, d), { center: { x: d.center.x + delta.x, y: d.center.y + delta.y } }) });
                }
                return m;
            });
        });
        // Debounced saveState + announcement (500ms after last key press)
        if (keyMoveSaveTimer.current)
            clearTimeout(keyMoveSaveTimer.current);
        keyMoveSaveTimer.current = setTimeout(function () {
            var oldPos = keyMoveStartRef.current;
            var newPos = keyMoveCurPosRef.current;
            if (!oldPos || !newPos)
                return;
            saveState({
                type: id,
                id: id.split('_')[1],
                propertyName: 'center',
                old: JSON.stringify(oldPos),
                new: JSON.stringify(newPos),
            });
            var dx = Math.round(newPos.x - oldPos.x);
            var dy = Math.round(newPos.y - oldPos.y);
            var directions = [];
            if (dy < 0)
                directions.push("".concat(t('text.up'), " ").concat(Math.abs(dy)));
            if (dy > 0)
                directions.push("".concat(t('text.down'), " ").concat(dy));
            if (dx < 0)
                directions.push("".concat(t('text.left'), " ").concat(Math.abs(dx)));
            if (dx > 0)
                directions.push("".concat(t('text.right'), " ").concat(dx));
            var chainLen = chainId
                ? manipulativeRef.current.filter(function (m) {
                    return m.data.type === 'linkingCubes' &&
                        m.data.chainId === chainId;
                }).length
                : 0;
            var name = chainId
                ? t('text.chainOf', { count: chainLen })
                : t('text.linkingCube');
            setAnnounceText('');
            requestAnimationFrame(function () {
                return setAnnounceText(t('text.manipulativeMoved', {
                    name: name,
                    direction: directions.join(" ".concat(t('text.and'), " ")),
                    x: Math.round(newPos.x),
                    y: Math.round(newPos.y),
                }));
            });
            keyMoveStartRef.current = null;
            keyMoveCurPosRef.current = null;
        }, 500);
    }, [
        calledFrom,
        panningEnable,
        lock.editing,
        lock.positioning,
        isBaseOrStandalone,
        isInChainMember,
        chainId,
        color,
        id,
        center,
        orientation,
        setManipulative,
        setSelectedTool,
        setContextMenu,
        selectedTool,
        removeCubeAndRenumberChain,
        setContextMenu,
        setSelectedTool,
        performLink,
    ]);
    // ─── Mouse / Touch handlers ───────────────────────────────────────────
    var handleMouseDown = useCallback(function (e) {
        if (!isDraggable || panningEnable)
            return;
        // Always stop propagation so the canvas doesn't intercept
        e.stopPropagation();
        e.preventDefault();
        // If shift/ctrl/cmd held → selection-only, no drag
        var isModifier = 'shiftKey' in e && (e.shiftKey || e.ctrlKey || e.metaKey);
        if (isModifier)
            return;
        var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var clientY = 'clientX' in e ? e.clientY : e.touches[0].clientY;
        var draggedCubeIds;
        var currentManipulative = manipulativeRef.current;
        // Multi-selection drag: if 2+ cubes are selected and this cube is part
        // of the selection, drag the entire selection together as one unit.
        // Each selected cube belonging to a chain pulls in its full chain so
        // we don't accidentally tear a chain apart mid-drag.
        var selectedCubeIds = selectedTool.filter(function (sel) {
            return currentManipulative.some(function (m) { return m.data.id === sel && m.data.type === 'linkingCubes'; });
        });
        var isMultiSelectDrag = selectedCubeIds.length >= 2 && selectedCubeIds.includes(id);
        // Mid-chain cube grab tears off from that point ONLY for a pure
        // single-chain selection (just this chain's own cubes selected, or
        // nothing at all). When the selection is broader — another chain, or
        // a different manipulative type, also selected — grabbing any cube in
        // this chain should move the WHOLE chain together instead, matching
        // the isMultiSelectDrag branch below (which already pulls in every
        // member of this cube's chainId, plus any other selected chain's full
        // membership, when it runs).
        // Only relevant when THIS cube is itself already part of the current
        // selection — a stale selectedTool left over from clicking something
        // else earlier (a different chain, another manipulative) must not be
        // mistaken for "this chain is part of a broader multi-select" just
        // because it happens to still be populated.
        var hasBroaderSelection = selectedTool.includes(id) &&
            (selectedTool.length > selectedCubeIds.length ||
                selectedCubeIds.some(function (cid) {
                    if (cid === id)
                        return false;
                    var cube = currentManipulative.find(function (m) { return m.data.id === cid; });
                    var cd = cube === null || cube === void 0 ? void 0 : cube.data;
                    return !((cd === null || cd === void 0 ? void 0 : cd.chainId) && cd.chainId === chainId);
                }));
        var isMidChainCube = chainId !== undefined &&
            chainIndex !== undefined &&
            chainIndex !== 0 &&
            !hasBroaderSelection;
        // The subset of draggedCubeIds that belongs to THIS cube's own chain —
        // used to gate snap/link eligibility in onUp, separately from
        // draggedCubeIds (which also drives movement + the undo save and may
        // include other, incidentally-selected chains/cubes riding along).
        // Those other chains must never be force-merged into a snap target
        // just because they were part of the same drag gesture — see onUp.
        var ownChainIds;
        if (isMidChainCube) {
            var chainCubes = getChainCubesFromRef();
            // Defer breaking until first move so a plain click doesn't break
            // the chain or play the unlink sound.
            var idsToDrag_1 = new Set(chainCubes
                .filter(function (c) { var _a; return ((_a = c.chainIndex) !== null && _a !== void 0 ? _a : 0) >= chainIndex; })
                .map(function (c) { return c.id; }));
            ownChainIds = Array.from(idsToDrag_1);
            // Also pull in the full membership of any OTHER selected linkingCubes
            // chains/cubes — mirrors the isMultiSelectDrag branch below. Needed
            // because isMidChainCube takes priority over it and would otherwise
            // silently drop other selected chains from this drag's own save
            // batch: they still visually follow (via the different-source-leader
            // effect above), but that effect defers saving to this batch
            // whenever the source is itself linkingCubes (see its onEnd), so
            // omitting them here left them un-undoable.
            // Gated on selectedTool already including `id` for the same reason
            // as hasBroaderSelection above: selectedCubeIds can be >= 2 purely
            // from a stale/unrelated prior selection (e.g. chain A selected,
            // then a mid-chain cube of a DIFFERENT, unselected chain B is
            // grabbed) — without this gate, chain A would incorrectly get
            // dragged along with chain B's tear-off even though the user never
            // selected B at all.
            if (selectedTool.includes(id) && selectedCubeIds.length >= 2) {
                var _loop_2 = function (cubeId) {
                    if (cubeId === id || idsToDrag_1.has(cubeId))
                        return "continue";
                    var cube = currentManipulative.find(function (m) { return m.data.id === cubeId; });
                    if (!cube)
                        return "continue";
                    var cd = cube.data;
                    // Same chain as the one being torn — already fully handled by the
                    // tear-off tail above and the head-cube snapshot/diff logic below;
                    // merging it back in here would re-attach the head to the tail.
                    if (cd.chainId && cd.chainId === chainId)
                        return "continue";
                    if (cd.chainId) {
                        currentManipulative.forEach(function (m) {
                            if (m.data.type !== 'linkingCubes' || !m.data.id)
                                return;
                            if (m.data.chainId === cd.chainId) {
                                idsToDrag_1.add(m.data.id);
                            }
                        });
                    }
                    else {
                        idsToDrag_1.add(cubeId);
                    }
                };
                for (var _a = 0, selectedCubeIds_1 = selectedCubeIds; _a < selectedCubeIds_1.length; _a++) {
                    var cubeId = selectedCubeIds_1[_a];
                    _loop_2(cubeId);
                }
            }
            draggedCubeIds = Array.from(idsToDrag_1);
        }
        else if (isMultiSelectDrag) {
            var idsToDrag_2 = new Set();
            var _loop_3 = function (cubeId) {
                var cube = currentManipulative.find(function (m) { return m.data.id === cubeId; });
                if (!cube)
                    return "continue";
                var cd = cube.data;
                if (cd.chainId) {
                    currentManipulative.forEach(function (m) {
                        if (m.data.type !== 'linkingCubes' || !m.data.id)
                            return;
                        if (m.data.chainId === cd.chainId) {
                            idsToDrag_2.add(m.data.id);
                        }
                    });
                }
                else {
                    idsToDrag_2.add(cubeId);
                }
            };
            for (var _b = 0, selectedCubeIds_2 = selectedCubeIds; _b < selectedCubeIds_2.length; _b++) {
                var cubeId = selectedCubeIds_2[_b];
                _loop_3(cubeId);
            }
            draggedCubeIds = Array.from(idsToDrag_2);
            // Own chain = whichever chain THIS cube (the one actually grabbed)
            // belongs to — other selected chains merged in above must not be
            // eligible to snap/link, only ride along for movement + undo.
            ownChainIds = chainId
                ? getChainCubesFromRef().map(function (c) { return c.id; })
                : [id];
        }
        else if (chainId && chainIndex !== undefined) {
            // Base cube (index 0): drag entire chain
            draggedCubeIds = getChainCubesFromRef().map(function (c) { return c.id; });
            ownChainIds = draggedCubeIds;
        }
        else if (chainId) {
            // Standalone chained cube (no chainIndex): drag entire chain
            draggedCubeIds = getChainCubesFromRef().map(function (c) { return c.id; });
            ownChainIds = draggedCubeIds;
        }
        else {
            draggedCubeIds = [id];
            ownChainIds = [id];
        }
        // If any cube in the dragged set is locked, block the entire drag
        var anyLocked = draggedCubeIds.some(function (cubeId) {
            var cubeItem = currentManipulative.find(function (m) { return m.data.id === cubeId; });
            return cubeItem && cubeItem.data.lock.positioning;
        });
        if (anyLocked)
            return;
        // Capture start positions and full item snapshots for undo/redo
        var startPositions = new Map();
        var startItems = new Map();
        var _loop_4 = function (cubeId) {
            var cubeItem = currentManipulative.find(function (m) { return m.data.id === cubeId; });
            if (cubeItem) {
                startPositions.set(cubeId, {
                    x: cubeItem.data.center.x,
                    y: cubeItem.data.center.y,
                });
                startItems.set(cubeId, cubeItem);
            }
        };
        for (var _d = 0, draggedCubeIds_1 = draggedCubeIds; _d < draggedCubeIds_1.length; _d++) {
            var cubeId = draggedCubeIds_1[_d];
            _loop_4(cubeId);
        }
        // Also snapshot the HEAD cubes left behind by a mid-chain tear-off
        // (chainIndex < the grabbed cube's). They never move, so they're
        // deliberately excluded from startPositions/draggedCubeIds — but
        // breakChainAt (below, on first move) silently dissolves a lone head
        // cube's chainId when it's the only one left on that side. Snapshotting
        // them here lets onUp fold that change into the same undo batch instead
        // of it going completely un-historied.
        var headCubeIds = isMidChainCube
            ? getChainCubesFromRef()
                .filter(function (c) { var _a; return ((_a = c.chainIndex) !== null && _a !== void 0 ? _a : 0) < chainIndex; })
                .map(function (c) { return c.id; })
            : [];
        var _loop_5 = function (cubeId) {
            var cubeItem = currentManipulative.find(function (m) { return m.data.id === cubeId; });
            if (cubeItem)
                startItems.set(cubeId, cubeItem);
        };
        for (var _e = 0, headCubeIds_1 = headCubeIds; _e < headCubeIds_1.length; _e++) {
            var cubeId = headCubeIds_1[_e];
            _loop_5(cubeId);
        }
        // Mid-chain grab schedules a break on first move regardless of
        // multi-select state — see `isMidChainCube` comment above.
        var pendingBreak = isMidChainCube
            ? { chainId: chainId, chainIndex: chainIndex }
            : null;
        dragRef.current = {
            x: clientX,
            y: clientY,
            startPositions: startPositions,
            startItems: startItems,
            draggedCubeIds: draggedCubeIds,
            headCubeIds: headCubeIds,
            ownChainIds: ownChainIds,
            moved: false,
            pendingBreak: pendingBreak,
        };
        // Select cubes being dragged — but only when this cube isn't ALREADY part of a
        // broader multi-select (e.g. this chain + a dice + a tile). Unconditionally
        // overwriting selectedTool here wiped out every other selected item the instant
        // you mousedown'd on the cube to start dragging it, deselecting all its followers.
        var alreadyInBroaderSelection = selectedTool.length > 1 && selectedTool.includes(id);
        if (!alreadyInBroaderSelection) {
            setSelectedTool(draggedCubeIds);
        }
        customEventHandler.dispatchEvent('linkingCubeDragActive', draggedCubeIds);
        startMultiDrag();
        // ─── Attach window listeners for this drag session ───
        var onMove = function (ev) {
            var drag = dragRef.current;
            if (!drag)
                return;
            var cx = 'clientX' in ev ? ev.clientX : ev.touches[0].clientX;
            var cy = 'clientX' in ev ? ev.clientY : ev.touches[0].clientY;
            var dragDist = Math.sqrt(Math.pow((cx - drag.x), 2) + Math.pow((cy - drag.y), 2));
            if (dragDist < 5)
                return;
            if (!drag.moved && drag.pendingBreak) {
                breakChainAt(drag.pendingBreak.chainId, drag.pendingBreak.chainIndex);
                playAudio('cubeUnlink');
                drag.pendingBreak = null;
                // The head cubes left behind by the tear-off were never removed from
                // selectedTool (mousedown's alreadyInBroaderSelection guard skips
                // narrowing it here) — without this, they keep riding along on every
                // later drag of the torn-off tail via the isMultiSelectDrag branch in
                // handleMouseDown, even though they're no longer actually linked.
                if (drag.headCubeIds.length > 0) {
                    setSelectedTool(function (prev) { return prev.filter(function (sid) { return !drag.headCubeIds.includes(sid); }); });
                }
            }
            drag.moved = true;
            var z = zoomRef.current || 1;
            var dx = (cx - drag.x) / z;
            var dy = (cy - drag.y) / z;
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    var sp = item.data.id ? drag.startPositions.get(item.data.id) : undefined;
                    if (!sp)
                        return item;
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: sp.x + dx, y: sp.y + dy } }) });
                });
            });
            // Multi-select: broadcast delta so OTHER-type items follow
            moveMultiDrag(dx, dy);
        };
        var onUp = function () {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
            if (!dragRef.current)
                return;
            var ids = dragRef.current.draggedCubeIds;
            var _a = dragRef.current, startItems = _a.startItems, headCubeIds = _a.headCubeIds, ownChainIds = _a.ownChainIds, moved = _a.moved;
            if (moved) {
                justDraggedRef.current = true;
                setTimeout(function () {
                    justDraggedRef.current = false;
                }, 0);
            }
            // Snap detection on drop — check both first and last cube of THIS
            // cube's own chain (ownChainIds), not the full dragged set (ids),
            // which may also include other, incidentally-selected chains riding
            // along for movement/undo. Those must never be eligible to merge
            // into a snap target just because they were part of this gesture —
            // see linkToTarget below. `ids` is still used as excludeIds so no
            // actively-dragged cube (own chain or otherwise) is mistaken for a
            // stationary snap target.
            var snapped = false;
            if (ownChainIds.length > 0 && moved) {
                var latestManip = manipulativeRef.current;
                var firstCube = latestManip.find(function (m) { return m.data.id === ownChainIds[0]; });
                var lastCube = ownChainIds.length > 1
                    ? latestManip.find(function (m) { return m.data.id === ownChainIds[ownChainIds.length - 1]; })
                    : null;
                var snap = null;
                if (firstCube) {
                    var cd = firstCube.data;
                    snap = detectSnap(cd.center, cd.orientation, ids);
                }
                if (!snap && lastCube) {
                    var cd = lastCube.data;
                    snap = detectSnap(cd.center, cd.orientation, ids);
                }
                if (snap) {
                    snapped = true;
                    // Only THIS cube's own chain is eligible to merge into the snap
                    // target — other selected chains/cubes in `ids` are just along
                    // for the ride and must never be force-relinked into a
                    // different chain/orientation (see idsForSave below for their
                    // own separate position save).
                    linkToTarget(snap, ownChainIds, startItems);
                    playAudio('cubeLink');
                    var targetChainId_1 = snap.targetCube.chainId;
                    var existingChainIds = targetChainId_1
                        ? latestManip
                            .filter(function (m) {
                            return m.data.type === 'linkingCubes' &&
                                m.data.chainId === targetChainId_1;
                        })
                            .map(function (m) { return m.data.id; })
                            .filter(function (cid) { return cid !== undefined; })
                        : [snap.targetCube.id];
                    // Select entire resulting chain for unified overlay — merged with everything
                    // else that was already selected (other-type items like dice/tiles, AND any
                    // OTHER, unrelated linkingCubes selection — e.g. the stationary segment left
                    // behind by a mid-chain break — that isn't part of this drag or the merge).
                    // Only drop ids that are actually being SUPERSEDED: the dragged cubes and the
                    // target chain's own (pre-merge) ids. A blanket "drop every linkingCubes id"
                    // filter here previously deselected that unrelated stationary segment too.
                    var supersededIds_1 = new Set(__spreadArray(__spreadArray([], ids, true), existingChainIds, true));
                    var otherSelected = selectedToolRef.current.filter(function (sel) { return !supersededIds_1.has(sel); });
                    setSelectedTool(Array.from(new Set(__spreadArray(__spreadArray(__spreadArray([], otherSelected, true), existingChainIds, true), ids, true))));
                }
                // Save position change and bring-to-front reorder for undo/redo as a
                // single batch entry so the entire chain undoes/redoes in one step.
                // When snapped, linkToTarget already saved its own multipleCompleteData
                // entry covering ownChainIds (the merged chain) — exclude those here to
                // avoid double-saving, but any OTHER selected chains/cubes riding along
                // in `ids` (not merged, since only ownChainIds is eligible to link)
                // still moved and still need their own position entry.
                var idsForSave_2 = snapped ? ids.filter(function (cubeId) { return !ownChainIds.includes(cubeId); }) : ids;
                if (idsForSave_2.length > 0) {
                    var currentManip = manipulativeRef.current;
                    var oldItems = [];
                    var newItems = [];
                    var _loop_6 = function (cubeId) {
                        var oldItem = startItems.get(cubeId);
                        var newItem = currentManip.find(function (m) { return m.data.id === cubeId; });
                        if (oldItem && newItem) {
                            oldItems.push(oldItem);
                            newItems.push(newItem);
                        }
                    };
                    for (var _b = 0, idsForSave_1 = idsForSave_2; _b < idsForSave_1.length; _b++) {
                        var cubeId = idsForSave_1[_b];
                        _loop_6(cubeId);
                    }
                    // Head cubes left behind by a mid-chain tear-off don't move, but
                    // breakChainAt may have silently dissolved a lone head cube's
                    // chainId (leftSideSingleton case) — fold any actual change in
                    // for those into this same undo batch so it isn't un-historied.
                    // (Already covered by linkToTarget's own save when snapped, since
                    // headCubeIds only ever arise from THIS cube's own chain.)
                    if (!snapped) {
                        var _loop_7 = function (cubeId) {
                            var oldItem = startItems.get(cubeId);
                            var newItem = currentManip.find(function (m) { return m.data.id === cubeId; });
                            if (oldItem &&
                                newItem &&
                                JSON.stringify(oldItem) !== JSON.stringify(newItem)) {
                                oldItems.push(oldItem);
                                newItems.push(newItem);
                            }
                        };
                        for (var _d = 0, headCubeIds_2 = headCubeIds; _d < headCubeIds_2.length; _d++) {
                            var cubeId = headCubeIds_2[_d];
                            _loop_7(cubeId);
                        }
                    }
                    var batchEntries = [];
                    if (oldItems.length > 0) {
                        batchEntries.push({
                            type: 'multipleManipulatives',
                            id: idsForSave_2.join(','),
                            propertyName: 'multipleCompleteData',
                            old: JSON.stringify(oldItems),
                            new: JSON.stringify(newItems),
                        });
                    }
                    // Move dragged cubes to end of array so they render on top after drop.
                    var beforeIds = currentManip.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; });
                    var afterIds = __spreadArray(__spreadArray([], beforeIds.filter(function (bid) { return !idsForSave_2.includes(bid); }), true), idsForSave_2, true);
                    var reorderChanged = beforeIds.join(',') !== afterIds.join(',');
                    if (reorderChanged) {
                        batchEntries.push({
                            type: 'multipleManipulatives',
                            id: idsForSave_2.join(','),
                            propertyName: 'reorder',
                            old: JSON.stringify(beforeIds),
                            new: JSON.stringify(afterIds),
                        });
                    }
                    if (batchEntries.length > 0 &&
                        !registerMultiSelectSaveEntries(id, batchEntries)) {
                        saveState(batchEntries);
                    }
                    if (reorderChanged) {
                        // Functional update against live `prev` — cube positions were
                        // committed continuously during onMove; replacing state with
                        // objects built from currentManip (a ref snapshot) here would
                        // risk clobbering a position update the ref hasn't caught up to.
                        setManipulative(function (prev) { return bringManipulativesToFront(prev, idsForSave_2); });
                    }
                }
            }
            endMultiDrag();
            dragRef.current = null;
            customEventHandler.dispatchEvent('linkingCubeDragActive', []);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove);
        window.addEventListener('touchend', onUp);
    }, [
        isDraggable,
        panningEnable,
        chainId,
        chainIndex,
        id,
        selectedTool,
        setManipulative,
        setSelectedTool,
        getChainCubesFromRef,
        startMultiDrag,
        moveMultiDrag,
        endMultiDrag,
    ]);
    var handleClick = useCallback(function (e) {
        if (panningEnable)
            return;
        e.stopPropagation();
        // The browser fires this click right after mouseup even when a real drag just
        // happened — skip it so a completed drag doesn't collapse the selection down to
        // just this cube/chain (see justDraggedRef's declaration for the full explanation).
        if (justDraggedRef.current)
            return;
        var isMultiSelect = e.shiftKey || e.ctrlKey || e.metaKey;
        if (isMultiSelect) {
            // Toggle this cube in selection
            if (selectedTool.includes(id)) {
                setSelectedTool(selectedTool.filter(function (s) { return s !== id; }));
            }
            else {
                setSelectedTool(__spreadArray(__spreadArray([], selectedTool, true), [id], false));
            }
        }
        else if (chainId) {
            // Plain click on chained cube: select entire chain
            var chainIds = getChainCubesFromRef().map(function (c) { return c.id; });
            setSelectedTool(chainIds);
        }
        else {
            setSelectedTool([id]);
        }
    }, [panningEnable, selectedTool, id, chainId, setSelectedTool, getChainCubesFromRef]);
    // ─── Render ──��────────────────────────────────────────────────────────
    var cubeImgSrc = getCubeImgSrc(orientation, color);
    var colorName = COLOR_HEX_TO_NAME[color] || 'cube';
    // Chain context for aria-label
    var chainCount = chainId
        ? manipulative.filter(function (m) { return m.data.type === 'linkingCubes' && m.data.chainId === chainId; }).length
        : 0;
    var isChainBase = !!chainId && (chainIndex !== null && chainIndex !== void 0 ? chainIndex : 0) === 0;
    var cubeLabel = (function () {
        var selectedSuffix = isSelected ? ", ".concat(t('text.selected')) : '';
        // Link-picker mode: candidate gets a label that reads candidate position
        // plus the arrow-key instructions for placing the source cube.
        if (isLinkCandidate && linkPicker) {
            var candidateLabel = t('text.linkingCubeLinkPickerCandidate', {
                current: linkPicker.currentIndex + 1,
                total: linkPicker.candidateIds.length,
                color: colorName,
                orientation: orientation,
            });
            return "".concat(candidateLabel, ", ").concat(t('text.linkingCubeLinkPickerStart'));
        }
        if (isInChainMember) {
            // Mid-chain cube is decorative — it's aria-hidden and not focusable, so
            // the label is never read by SR. Leave a minimal string for DOM inspectors.
            return '';
        }
        if (isChainBase) {
            // Chain base speaks for the entire chain. Read every cube's color in
            // visual order (sorted by chainIndex) so SR users hear the full chain.
            var chainMembers = manipulative
                .filter(function (m) { return m.data.type === 'linkingCubes' && m.data.chainId === chainId; })
                .map(function (m) { return m.data; })
                .sort(function (a, b) { var _a, _b; return ((_a = a.chainIndex) !== null && _a !== void 0 ? _a : 0) - ((_b = b.chainIndex) !== null && _b !== void 0 ? _b : 0); });
            var colors = chainMembers
                .map(function (c) { var _a; return (_a = COLOR_HEX_TO_NAME[c.color]) !== null && _a !== void 0 ? _a : 'cube'; })
                .join(', ');
            return "".concat(t('text.linkingCubeChainLabel', {
                count: chainCount,
                orientation: orientation,
                colors: colors,
            })).concat(selectedSuffix);
        }
        // Standalone
        return "".concat(t('text.linkingCubeStandalone', {
            color: colorName,
            orientation: orientation,
        })).concat(selectedSuffix).concat(lock.editing ? '' : " ".concat(t('text.linkingCubeStandaloneHint')));
    })();
    // A linked chain reads to NVDA / JAWS as a single interactive unit:
    //   • The BASE cube (chainIndex 0) is the only Tab stop — its aria-label
    //     describes the entire chain ("horizontal chain of 4 cubes: red,
    //     red, blue, brown").
    //   • Mid-chain cubes get tabIndex=-1 and aria-hidden=true so they're
    //     skipped by both Tab navigation AND NVDA's reading order, instead
    //     of being announced as separate "linking cube, button" entries.
    //   • Standalone cubes stay individually focusable.
    // Locked cubes / chains REMAIN Tab-focusable; locking only restricts
    // movement actions (they must still reach a locked cube to unlock it).
    // During link-picker mode NO cube is focusable — only the dummy "Link
    // here" buttons.
    var cubeTabIndex = (function () {
        if (linkPicker)
            return -1;
        if (calledFrom !== 'widget')
            return -1;
        if (isInChainMember)
            return -1;
        return 0;
    })();
    var cubeRole = calledFrom !== 'widget' ? 'img' : 'button';
    var cubeRoleDescription = calledFrom !== 'widget' ? undefined : t('text.linkingCube');
    var cubeAriaHidden = isInChainMember ? true : undefined;
    if (inChain) {
        return (_jsx("button", { ref: cubeRef, className: "linkingCube linkingCube--".concat(orientation, " ").concat(isSelected ? 'linkingCube--selected' : '', " ").concat(isCurrentLinkCandidate ? 'linkingCube--linkCandidate' : ''), style: { cursor: isDraggable && !isChainLocked && !panningEnable ? 'grab' : 'default' }, "data-id": id, draggable: false, onDragStart: function (e) { return e.preventDefault(); }, onMouseDown: handleMouseDown, onTouchStart: handleMouseDown, onClick: handleClick, tabIndex: cubeTabIndex, role: cubeRole, "aria-roledescription": cubeRoleDescription, "aria-label": cubeLabel || undefined, "aria-hidden": cubeAriaHidden, "aria-pressed": isSelected, onKeyDown: calledFrom === 'widget' ? handleCubeKeyDown : undefined, onFocus: function () { return setIsFocused(true); }, onBlur: function () { return setIsFocused(false); }, children: _jsx("img", { src: cubeImgSrc, alt: "", draggable: false, style: { width: '100%', height: '100%', pointerEvents: 'none' } }) }));
    }
    // Standalone cube
    var scaledSize = CUBE_SIZE * scale;
    // Compute the chain's bounding box (shared by the selection + focus overlays).
    // For standalones this is a single-cube box; for chains it spans all members.
    // Mid-chain members return null — they don't render overlays.
    var chainBoundingBox = useMemo(function () {
        if (isInChainMember)
            return null;
        // The cube wrapper sits at `center − CUBE_SIZE/2` (the grid pitch),
        // and the rendered cube image inside is CSS_CUBE_SIZE (65px) wide.
        // The 10-px difference is the interlock overlap between adjacent
        // cubes. So the BB's top-left must match the wrapper origin
        // (CUBE_SIZE/2 offset) while the BB's WIDTH spans the full image
        // size (CSS_CUBE_SIZE). Using CSS_CUBE_SIZE/2 for the offset would
        // shift the BB 5px left/up of the cubes — visible as a misaligned
        // focus outline.
        var half = (CUBE_SIZE * scale) / 2;
        var visualSize = CSS_CUBE_SIZE * scale;
        var chainCubes = chainId
            ? manipulative
                .filter(function (m) { return m.data.type === 'linkingCubes' && m.data.chainId === chainId; })
                .map(function (m) { return m.data; })
            : [{ center: center, chainIndex: 0 }];
        if (orientation === 'horizontal') {
            var xs = chainCubes.map(function (c) { return c.center.x; });
            return {
                left: Math.min.apply(Math, xs) - half,
                top: center.y - half,
                width: Math.max.apply(Math, xs) - Math.min.apply(Math, xs) + visualSize,
                height: visualSize,
            };
        }
        var ys = chainCubes.map(function (c) { return c.center.y; });
        return {
            left: center.x - half,
            top: Math.min.apply(Math, ys) - half,
            width: visualSize,
            height: Math.max.apply(Math, ys) - Math.min.apply(Math, ys) + visualSize,
        };
    }, [isInChainMember, chainId, manipulative, scale, orientation, center]);
    // ─── Chain focus indicator — draws a visible outline spanning the whole
    // chain when the base cube is focused. Gives keyboard/SR users a clear
    // visual cue that the chain (not just one cube) is the focused unit.
    var chainFocusOverlay = useMemo(function () {
        if (!isFocused || !chainBoundingBox)
            return null;
        return (_jsx("div", { className: "linkingCubesChainFocus", "aria-hidden": "true", style: __assign({ position: 'absolute' }, chainBoundingBox) }));
    }, [isFocused, chainBoundingBox]);
    // ─── Picker dummy buttons (link OR unlink mode) ─────────────────────────
    // Link mode: one dummy per candidate, overlaying the whole chain/cube
    //   bounding box. Tab cycles through them; Enter confirms; Escape cancels.
    // Unlink mode: a per-cube dummy over THIS cube's footprint (only for
    //   chain members at index >= 1). Selecting a dummy mirrors the mouse
    //   drag-break — `breakChainAt(chainId, k)` keeps cubes 0..k-1 on the
    //   original chain and moves k..end to a new chain. Focus moves to the
    //   newly-created chain's base (the picked cube).
    var linkDummyButtons = useMemo(function () {
        if (!linkPicker || !isLinkCandidate)
            return null;
        var isUnlinkMode = linkPicker.mode === 'unlink';
        if (!isUnlinkMode && !chainBoundingBox)
            return null;
        var sourceId = linkPicker.sourceId;
        var targetDesc = chainId
            ? t('text.linkingCubeChainDesc', { count: chainCount, color: colorName })
            : t('text.linkingCubeStandaloneDesc', { color: colorName, orientation: orientation });
        var focusDummy = function (delta) {
            var _a;
            var all = Array.from(document.querySelectorAll('[data-link-dummy]'));
            if (all.length === 0)
                return;
            var active = document.activeElement;
            var currentIdx = active ? all.indexOf(active) : -1;
            var n = all.length;
            var nextIdx = currentIdx < 0 ? 0 : (currentIdx + delta + n) % n;
            (_a = all[nextIdx]) === null || _a === void 0 ? void 0 : _a.focus();
        };
        var performUnlinkAt = function (k) {
            if (!chainId || k <= 0)
                return;
            // Snapshot the chain's full state for undo before mutating. Deep-copy
            // the center object so the saved oldItems don't reference the same
            // object we're about to mutate via setManipulative.
            var before = manipulativeRef.current.filter(function (m) {
                return m.data.type === 'linkingCubes' &&
                    m.data.chainId === chainId;
            });
            var oldItems = before.map(function (item) { return (__assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: __assign({}, item.data.center) }) })); });
            var newChainId = breakChainAt(chainId, k);
            // Visually offset the new chain so the user can SEE the split. Without
            // this the cubes stay at their pre-break positions and look identical
            // to before — exactly the screenshot complaint. The mouse path doesn't
            // hit this because the user's drag implicitly moves the new chain
            // away. Direction matches the chain's growth axis (column-reverse for
            // vertical → higher chainIndex is upward, so push further upward;
            // horizontal → push further right).
            var SPLIT_GAP = 30;
            var offset = orientation === 'horizontal'
                ? { x: SPLIT_GAP, y: 0 }
                : { x: 0, y: -SPLIT_GAP };
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    if (item.data.type !== 'linkingCubes')
                        return item;
                    var cd = item.data;
                    if (cd.chainId !== newChainId)
                        return item;
                    var shifted = {
                        x: cd.center.x + offset.x,
                        y: cd.center.y + offset.y,
                    };
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: shifted }) });
                });
            });
            playAudio('cubeUnlink');
            // Compute the after-state synchronously (mirrors both breakChainAt's
            // chainId/chainIndex update AND the offset shift above) so saveState
            // has a faithful redo target.
            var newItems = oldItems.map(function (item) {
                var _a, _b;
                var cd = item.data;
                if (((_a = cd.chainIndex) !== null && _a !== void 0 ? _a : 0) >= k) {
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { chainId: newChainId, chainIndex: ((_b = cd.chainIndex) !== null && _b !== void 0 ? _b : 0) - k, center: { x: cd.center.x + offset.x, y: cd.center.y + offset.y } }) });
                }
                return item;
            });
            saveState({
                type: 'multipleManipulatives',
                id: oldItems.map(function (o) { return o.data.id; }).join(','),
                propertyName: 'multipleCompleteData',
                old: JSON.stringify(oldItems),
                new: JSON.stringify(newItems),
            });
            setLinkPickerState(null);
            // Return focus to the cube the user picked — now the new chain's base.
            var pickedId = id;
            requestAnimationFrame(function () {
                var el = document.querySelector("[data-id=\"".concat(pickedId, "\"]"));
                el === null || el === void 0 ? void 0 : el.focus();
            });
        };
        var handleDummyKey = function (e) {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                setLinkPickerState(null);
                announce(t('text.linkingCubeLinkPickerCancelled'));
                requestAnimationFrame(function () {
                    var src = document.querySelector("[data-id=\"".concat(sourceId, "\"]"));
                    src === null || src === void 0 ? void 0 : src.focus();
                });
                return;
            }
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                if (isUnlinkMode) {
                    performUnlinkAt(chainIndex !== null && chainIndex !== void 0 ? chainIndex : 0);
                }
                else {
                    performLink(sourceId, id, 'append');
                }
                return;
            }
            // Arrow keys and Tab move between candidate dummies.
            if (e.key === 'ArrowDown' ||
                e.key === 'ArrowRight' ||
                (e.key === 'Tab' && !e.shiftKey)) {
                e.preventDefault();
                e.stopPropagation();
                focusDummy(1);
                return;
            }
            if (e.key === 'ArrowUp' ||
                e.key === 'ArrowLeft' ||
                (e.key === 'Tab' && e.shiftKey)) {
                e.preventDefault();
                e.stopPropagation();
                focusDummy(-1);
                return;
            }
        };
        if (isUnlinkMode) {
            // Per-cube overlay over THIS cube's footprint. Tight to cube edges
            // (1px out) so the focus outline visually points at the picked cube
            // without bleeding into adjacent cubes — adjacent cubes' dummies
            // would otherwise overlap and the SR-focused dummy would look
            // misaligned. Positioning is world coords because this dummy is a
            // sibling of the wrapper, not inside it.
            var unlinkPad = 1;
            // Align with the cube *image* (CSS_CUBE_SIZE wide) anchored at the
            // cube *wrapper*'s left/top (center − CUBE_SIZE/2). Using the visual
            // half-width (CSS_CUBE_SIZE/2) for the offset would shift the dummy
            // ~5px off the cube — the misaligned highlight in the screenshot.
            var halfWrapper = (CUBE_SIZE * scale) / 2;
            var visualSize = CSS_CUBE_SIZE * scale;
            var overlayStyle_1 = {
                position: 'absolute',
                left: center.x - halfWrapper - unlinkPad,
                top: center.y - halfWrapper - unlinkPad,
                width: visualSize + unlinkPad * 2,
                height: visualSize + unlinkPad * 2,
            };
            var k_1 = chainIndex !== null && chainIndex !== void 0 ? chainIndex : 0;
            var chainAll = manipulativeRef.current.filter(function (m) {
                return m.data.type === 'linkingCubes' &&
                    m.data.chainId === chainId;
            }).length;
            // belowCount = cubes that detach (chainIndex >= k → all from k onward).
            var belowCount = Math.max(0, chainAll - k_1);
            return (_jsx("button", { className: "linkingCubeDummy linkingCubeDummy--overlay linkingCubeDummy--unlink", "data-link-dummy": id, style: overlayStyle_1, "aria-label": t('text.linkingCubeUnlinkDummyOverlay', {
                    color: colorName,
                    position: k_1 + 1,
                    total: chainAll,
                    below: belowCount,
                    plural: belowCount === 1 ? '' : 's',
                }), tabIndex: 0, onClick: function () { return performUnlinkAt(k_1); }, onKeyDown: handleDummyKey }));
        }
        // Link mode — chain bounding-box overlay.
        var pad = 8;
        var overlayStyle = {
            position: 'absolute',
            left: chainBoundingBox.left - pad,
            top: chainBoundingBox.top - pad,
            width: chainBoundingBox.width + pad * 2,
            height: chainBoundingBox.height + pad * 2,
        };
        return (_jsx("button", { className: "linkingCubeDummy linkingCubeDummy--overlay", "data-link-dummy": id, style: overlayStyle, "aria-label": t('text.linkingCubeDummyOverlay', { target: targetDesc }), tabIndex: 0, onClick: function () { return performLink(sourceId, id, 'append'); }, onKeyDown: handleDummyKey }));
    }, [
        linkPicker,
        isLinkCandidate,
        chainBoundingBox,
        chainId,
        chainIndex,
        chainCount,
        colorName,
        orientation,
        center.x,
        center.y,
        scale,
        id,
        performLink,
    ]);
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "linkingCubesChain ".concat(showIndividualSelection ? 'linkingCubesChain--selected' : '', " ").concat(lock.positioning ? 'locked' : ''), "data-manipulative-id": id, style: {
                    left: center.x - scaledSize / 2,
                    top: center.y - scaledSize / 2,
                    transform: "scale(".concat(scale, ")"),
                    transformOrigin: 'top left',
                    // CSS-only bring-to-front while actively dragging — the permanent
                    // stacking order comes from the `manipulative` array position
                    // (reordered + saveState-tracked on drop), not from zIndex.
                    // `chainIndex` offset preserves each cube's relative order within
                    // its own chain while the whole chain is boosted above everything else.
                    zIndex: isDragging ? 9999 + (chainIndex !== null && chainIndex !== void 0 ? chainIndex : 0) : undefined,
                }, children: [calledFrom === 'widget' && IS_TOUCH_DEVICE && (function () {
                        if (!chainId)
                            return true; // standalone — render its own overlay
                        var chainMembers = manipulative.filter(function (m) { return m.data.type === 'linkingCubes' && m.data.chainId === chainId; });
                        var maxIdx = chainMembers.length
                            ? Math.max.apply(Math, chainMembers.map(function (m) { var _a; return (_a = m.data.chainIndex) !== null && _a !== void 0 ? _a : 0; })) : 0;
                        return (chainIndex !== null && chainIndex !== void 0 ? chainIndex : 0) === maxIdx;
                    })() && (_jsx(SelectFocusOverlay, { id: id, hidden: lock.editing || (!isSelected && !isFocused), className: "linkingCubeSelectBtn", title: t('text.selectCube'), onAfterClick: function () {
                            var _a;
                            var rect = (_a = cubeRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                            if (rect) {
                                setContextMenu({
                                    x: rect.right,
                                    y: rect.top,
                                    manipulativeId: id,
                                });
                            }
                        } })), _jsx("button", { ref: cubeRef, className: "linkingCube linkingCube--".concat(orientation, " ").concat(isCurrentLinkCandidate ? 'linkingCube--linkCandidate' : ''), style: {
                            cursor: isDraggable && !isChainLocked && !panningEnable ? 'grab' : 'default',
                        }, "data-id": id, draggable: false, onDragStart: function (e) { return e.preventDefault(); }, onMouseDown: handleMouseDown, onTouchStart: handleMouseDown, onClick: handleClick, tabIndex: cubeTabIndex, role: cubeRole, "aria-roledescription": cubeRoleDescription, "aria-label": cubeLabel || undefined, "aria-hidden": cubeAriaHidden, "aria-pressed": isSelected, onKeyDown: calledFrom === 'widget' ? handleCubeKeyDown : undefined, onFocus: function () { return setIsFocused(true); }, onBlur: function () { return setIsFocused(false); }, children: _jsx("img", { src: cubeImgSrc, alt: "", draggable: false, style: { width: '100%', height: '100%', pointerEvents: 'none' } }) })] }), chainFocusOverlay, linkDummyButtons, calledFrom === 'widget' && !isInChainMember && isFocused && (_jsx("div", { "aria-live": "polite", "aria-atomic": "true", style: {
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    padding: 0,
                    margin: -1,
                    overflow: 'hidden',
                    clip: 'rect(0,0,0,0)',
                    whiteSpace: 'nowrap',
                    border: 0,
                }, children: announceText }))] }));
};
export default LinkingCubes;
// ─── Utility: snap detection (pure function) ─────────────────────────────
export var detectSnapTarget = function (cubeCenter, cubeOrientation, excludeIds, items, cubeScale) {
    var _a, _b, _d, _e, _f, _g, _h;
    if (cubeScale === void 0) { cubeScale = 1; }
    // Track chain ends (max index) and chain starts (min index) per chainId
    var chainMaxIndex = new Map();
    var chainMinIndex = new Map();
    for (var _j = 0, items_1 = items; _j < items_1.length; _j++) {
        var item = items_1[_j];
        if (item.data.type !== 'linkingCubes')
            continue;
        var cd = item.data;
        if (!cd.chainId)
            continue;
        var idx = (_a = cd.chainIndex) !== null && _a !== void 0 ? _a : 0;
        var curMax = (_b = chainMaxIndex.get(cd.chainId)) !== null && _b !== void 0 ? _b : -1;
        if (idx > curMax)
            chainMaxIndex.set(cd.chainId, idx);
        var curMin = chainMinIndex.get(cd.chainId);
        if (curMin === undefined || idx < curMin)
            chainMinIndex.set(cd.chainId, idx);
    }
    var ourScaledHalf = (CUBE_SIZE * cubeScale) / 2;
    var bestResult = null;
    var bestDist = Infinity;
    for (var _k = 0, items_2 = items; _k < items_2.length; _k++) {
        var item = items_2[_k];
        if (item.data.type !== 'linkingCubes')
            continue;
        var target = item.data;
        if (excludeIds.includes(target.id))
            continue;
        if (target.orientation !== cubeOrientation)
            continue;
        var targetScale = (_d = target.scale) !== null && _d !== void 0 ? _d : 1;
        var scaledSize = CUBE_SIZE * targetScale;
        var overlapBuffer = scaledSize / 2;
        var isChainEnd = !target.chainId || ((_e = target.chainIndex) !== null && _e !== void 0 ? _e : 0) === ((_f = chainMaxIndex.get(target.chainId)) !== null && _f !== void 0 ? _f : 0);
        var isChainStart = !target.chainId || ((_g = target.chainIndex) !== null && _g !== void 0 ? _g : 0) === ((_h = chainMinIndex.get(target.chainId)) !== null && _h !== void 0 ? _h : 0);
        if (cubeOrientation === 'horizontal') {
            var yDiff = Math.abs(cubeCenter.y - target.center.y);
            if (yDiff >= SNAP_ALIGN_THRESHOLD)
                continue;
            // Snap to right end (after)
            if (isChainEnd) {
                var ourLeft = cubeCenter.x - ourScaledHalf;
                var targetRight = target.center.x + scaledSize / 2;
                var gap = ourLeft - targetRight;
                if (gap < SNAP_THRESHOLD && gap > -overlapBuffer) {
                    var dist = Math.abs(gap) + yDiff;
                    if (dist < bestDist) {
                        bestDist = dist;
                        bestResult = {
                            targetCube: target,
                            snappedPosition: { x: target.center.x + scaledSize, y: target.center.y },
                            side: 'after',
                        };
                    }
                }
            }
            // Snap to left start (before)
            if (isChainStart) {
                var ourRight = cubeCenter.x + ourScaledHalf;
                var targetLeft = target.center.x - scaledSize / 2;
                var gap = targetLeft - ourRight;
                if (gap < SNAP_THRESHOLD && gap > -overlapBuffer) {
                    var dist = Math.abs(gap) + yDiff;
                    if (dist < bestDist) {
                        bestDist = dist;
                        bestResult = {
                            targetCube: target,
                            snappedPosition: { x: target.center.x - scaledSize, y: target.center.y },
                            side: 'before',
                        };
                    }
                }
            }
        }
        else {
            var xDiff = Math.abs(cubeCenter.x - target.center.x);
            if (xDiff >= SNAP_ALIGN_THRESHOLD)
                continue;
            // Snap to top end (after — vertical chains grow upward)
            if (isChainEnd) {
                var ourBottom = cubeCenter.y + ourScaledHalf;
                var targetTop = target.center.y - scaledSize / 2;
                var gap = targetTop - ourBottom;
                if (gap < SNAP_THRESHOLD && gap > -overlapBuffer) {
                    var dist = Math.abs(gap) + xDiff;
                    if (dist < bestDist) {
                        bestDist = dist;
                        bestResult = {
                            targetCube: target,
                            snappedPosition: { x: target.center.x, y: target.center.y - scaledSize },
                            side: 'after',
                        };
                    }
                }
            }
            // Snap to bottom start (before)
            if (isChainStart) {
                var ourTop = cubeCenter.y - ourScaledHalf;
                var targetBottom = target.center.y + scaledSize / 2;
                var gap = ourTop - targetBottom;
                if (gap < SNAP_THRESHOLD && gap > -overlapBuffer) {
                    var dist = Math.abs(gap) + xDiff;
                    if (dist < bestDist) {
                        bestDist = dist;
                        bestResult = {
                            targetCube: target,
                            snappedPosition: { x: target.center.x, y: target.center.y + scaledSize },
                            side: 'before',
                        };
                    }
                }
            }
        }
    }
    return bestResult;
};
// ─── Utility: link selected cubes into a chain ──────────────────────────
export var linkSelectedCubes = function (selectedCubeIds, manipulative, setManipulative) {
    var _a;
    var selectedCubes = manipulative
        .filter(function (m) {
        return m.data.id !== undefined &&
            selectedCubeIds.includes(m.data.id) &&
            m.data.type === 'linkingCubes';
    })
        .map(function (m) { return m.data; });
    if (selectedCubes.length < 2)
        return false;
    var orientation = selectedCubes[0].orientation;
    if (!selectedCubes.every(function (c) { return c.orientation === orientation; }))
        return false;
    // Sort by position
    var sorted = __spreadArray([], selectedCubes, true).sort(function (a, b) {
        return orientation === 'horizontal' ? a.center.x - b.center.x : b.center.y - a.center.y;
    });
    var newChainId = "chain_".concat(uuidv4().replace(/-/g, ''));
    var chainScale = (_a = sorted[0].scale) !== null && _a !== void 0 ? _a : 1;
    var scaledSize = CUBE_SIZE * chainScale;
    var baseCenter = sorted[0].center;
    // Build the sorted set of IDs for quick lookup
    var sortedIds = new Set(sorted.map(function (s) { return s.id; }));
    // Capture old items and compute new items for saveState
    var oldItems = [];
    var newItems = [];
    setManipulative(function (prev) {
        // Remove linked cubes from their original positions
        var remaining = prev.filter(function (item) { return !(item.data.id !== undefined && sortedIds.has(item.data.id)); });
        // Build new linked cubes in chain order (sorted), appended at end
        var linkedCubes = sorted.map(function (s, index) {
            var original = prev.find(function (item) { return item.data.id === s.id; });
            oldItems.push(original);
            var newCenter = orientation === 'horizontal'
                ? { x: baseCenter.x + index * scaledSize, y: baseCenter.y }
                : { x: baseCenter.x, y: baseCenter.y - index * scaledSize };
            var newItem = __assign(__assign({}, original), { data: __assign(__assign({}, original.data), { chainId: newChainId, chainIndex: index, center: newCenter, scale: chainScale }) });
            newItems.push(newItem);
            return newItem;
        });
        return __spreadArray(__spreadArray([], remaining, true), linkedCubes, true);
    });
    saveState({
        type: 'multipleManipulatives',
        id: newChainId,
        propertyName: 'multipleCompleteData',
        old: JSON.stringify(oldItems),
        new: JSON.stringify(newItems),
    });
    return true;
};
// ─── Utility: link one cube to a target via menu selection ───────────────────
// focusedId: the cube the user is linking FROM (context-menu origin)
// targetId:  the cube the user picked to link TO
// side:      where the focused cube should end up relative to the target
//            - 'before' → focused becomes prior in chain order
//            - 'after'  → focused becomes after in chain order
// Handles both cases: target is a standalone cube, OR target is a chain endpoint.
export var linkCubesViaMenu = function (focusedId, targetId, side, manipulative, setManipulative) {
    var _a;
    var focusedItem = manipulative.find(function (m) { return m.data.id === focusedId; });
    var targetItem = manipulative.find(function (m) { return m.data.id === targetId; });
    if (!focusedItem || !targetItem)
        return false;
    if (focusedItem.data.type !== 'linkingCubes' ||
        targetItem.data.type !== 'linkingCubes') {
        return false;
    }
    var focused = focusedItem.data;
    var target = targetItem.data;
    if (focused.orientation !== target.orientation)
        return false;
    var chainScale = (_a = target.scale) !== null && _a !== void 0 ? _a : 1;
    var scaledSize = CUBE_SIZE * chainScale;
    // Source: if the focused cube is in a chain, treat the ENTIRE source chain
    // as the thing being moved + linked. Otherwise just the lone focused cube.
    var sourceMembers = focused.chainId
        ? manipulative
            .filter(function (m) {
            return m.data.type === 'linkingCubes' &&
                m.data.chainId === focused.chainId;
        })
            .map(function (m) { return m.data; })
            .sort(function (a, b) { var _a, _b; return ((_a = a.chainIndex) !== null && _a !== void 0 ? _a : 0) - ((_b = b.chainIndex) !== null && _b !== void 0 ? _b : 0); })
        : [focused];
    var sourceCount = sourceMembers.length;
    // Target endpoint where the source block lands.
    var anchorCenter;
    if (target.chainId) {
        var chainMembers = manipulative
            .filter(function (m) { return m.data.type === 'linkingCubes' && m.data.chainId === target.chainId; })
            .map(function (m) { return m.data; })
            .sort(function (a, b) { var _a, _b; return ((_a = a.chainIndex) !== null && _a !== void 0 ? _a : 0) - ((_b = b.chainIndex) !== null && _b !== void 0 ? _b : 0); });
        if (chainMembers.length === 0)
            return false;
        var first = chainMembers[0];
        var last = chainMembers[chainMembers.length - 1];
        if (side === 'before') {
            if (focused.orientation === 'horizontal') {
                anchorCenter = { x: first.center.x - scaledSize, y: first.center.y };
            }
            else {
                anchorCenter = { x: first.center.x, y: first.center.y + scaledSize };
            }
        }
        else {
            if (focused.orientation === 'horizontal') {
                anchorCenter = { x: last.center.x + scaledSize, y: last.center.y };
            }
            else {
                anchorCenter = { x: last.center.x, y: last.center.y - scaledSize };
            }
        }
    }
    else {
        if (side === 'before') {
            if (focused.orientation === 'horizontal') {
                anchorCenter = { x: target.center.x - scaledSize, y: target.center.y };
            }
            else {
                anchorCenter = { x: target.center.x, y: target.center.y + scaledSize };
            }
        }
        else {
            if (focused.orientation === 'horizontal') {
                anchorCenter = { x: target.center.x + scaledSize, y: target.center.y };
            }
            else {
                anchorCenter = { x: target.center.x, y: target.center.y - scaledSize };
            }
        }
    }
    // Compute final positions for source members, plus the merged chain order.
    // Source cubes pack against the target's anchor in the source's internal
    // order:
    //   - side 'after'  (append):  source[0] closest to anchor, extending away
    //   - side 'before' (prepend): source[last] closest to anchor, extending back
    var sourceNewCenters = new Map();
    sourceMembers.forEach(function (c, i) {
        var pos;
        if (focused.orientation === 'horizontal') {
            var offset = side === 'after'
                ? i * scaledSize
                : -(sourceCount - 1 - i) * scaledSize;
            pos = { x: anchorCenter.x + offset, y: anchorCenter.y };
        }
        else {
            // Vertical: chainIndex increases upward (y decreases).
            var offset = side === 'after'
                ? -i * scaledSize
                : (sourceCount - 1 - i) * scaledSize;
            pos = { x: anchorCenter.x, y: anchorCenter.y + offset };
        }
        sourceNewCenters.set(c.id, pos);
    });
    // Target chain members (or just the target if standalone).
    var targetMembers = target.chainId
        ? manipulative
            .filter(function (m) {
            return m.data.type === 'linkingCubes' &&
                m.data.chainId === target.chainId;
        })
            .map(function (m) { return m.data; })
            .sort(function (a, b) { var _a, _b; return ((_a = a.chainIndex) !== null && _a !== void 0 ? _a : 0) - ((_b = b.chainIndex) !== null && _b !== void 0 ? _b : 0); })
        : [target];
    // Merged chain order. For 'after' (append) source goes after target;
    // for 'before' (prepend) source goes first.
    var finalOrder = side === 'after'
        ? __spreadArray(__spreadArray([], targetMembers.map(function (c) { return c.id; }), true), sourceMembers.map(function (c) { return c.id; }), true) : __spreadArray(__spreadArray([], sourceMembers.map(function (c) { return c.id; }), true), targetMembers.map(function (c) { return c.id; }), true);
    var newChainId = "chain_".concat(uuidv4().replace(/-/g, ''));
    var orderIdx = new Map();
    finalOrder.forEach(function (cid, idx) { return orderIdx.set(cid, idx); });
    // Snapshot pre-state for undo/redo before mutation.
    var beforeSnapshot = manipulative
        .filter(function (m) { var _a; return m.data.type === 'linkingCubes' && orderIdx.has((_a = m.data.id) !== null && _a !== void 0 ? _a : ''); })
        .map(function (m) { return JSON.parse(JSON.stringify(m)); });
    // Assign chainId/chainIndex to every cube in the merged chain, and
    // reposition source cubes.
    var updated = manipulative.map(function (m) {
        var _a, _b, _d;
        if (m.data.type !== 'linkingCubes')
            return m;
        var idx = orderIdx.get((_a = m.data.id) !== null && _a !== void 0 ? _a : '');
        if (idx === undefined)
            return m;
        var newCenter = (_d = sourceNewCenters.get((_b = m.data.id) !== null && _b !== void 0 ? _b : '')) !== null && _d !== void 0 ? _d : m.data.center;
        return __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { chainId: newChainId, chainIndex: idx, center: newCenter, position: newCenter }) });
    });
    // Reorder the merged chain ascending by chainIndex so array position
    // matches the intended stacking (highest chainIndex = frontmost).
    var beforeIds = manipulative.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; });
    var reordered = reorderChainByIndex(updated, newChainId);
    var afterIds = reordered.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; });
    setManipulative(function () { return reordered; });
    var batchEntries = [
        {
            type: 'multipleManipulatives',
            id: newChainId,
            propertyName: 'multipleCompleteData',
            old: JSON.stringify(beforeSnapshot),
        },
    ];
    if (beforeIds.join(',') !== afterIds.join(',')) {
        batchEntries.push({
            type: 'multipleManipulatives',
            id: newChainId,
            propertyName: 'reorder',
            old: JSON.stringify(beforeIds),
            new: JSON.stringify(afterIds),
        });
    }
    saveState(batchEntries);
    // Focus the chain's NEW base (chainIndex 0) so the chain-wide focus
    // indicator renders. The source cube may now be a mid-chain member which
    // is aria-hidden / not focusable.
    var baseId = finalOrder[0];
    setTimeout(function () {
        var baseEl = document.querySelector("[data-id=\"".concat(baseId, "\"]"));
        baseEl === null || baseEl === void 0 ? void 0 : baseEl.focus();
    }, 50);
    return true;
};
// ─── Utility: unlink selected cubes from their chains ───────────────────
export var unlinkSelectedCubes = function (selectedCubeIds, manipulative, setManipulative) {
    // Gather the set of selected linking-cube IDs
    var selectedSet = new Set(selectedCubeIds);
    var affectedChainIds = new Set();
    var _loop_8 = function (cubeId) {
        var item = manipulative.find(function (m) { return m.data.id === cubeId; });
        if (item && item.data.type === 'linkingCubes' && item.data.chainId) {
            affectedChainIds.add(item.data.chainId);
        }
    };
    for (var _a = 0, selectedCubeIds_3 = selectedCubeIds; _a < selectedCubeIds_3.length; _a++) {
        var cubeId = selectedCubeIds_3[_a];
        _loop_8(cubeId);
    }
    if (affectedChainIds.size === 0)
        return false;
    // For each affected chain, figure out which cubes stay linked and which get detached.
    // Selected cubes become individual; the remaining cubes re-form contiguous sub-chains.
    var oldItems = [];
    var newItems = [];
    setManipulative(function (prev) {
        var _a;
        // First pass: collect chain membership info
        var chainMembers = new Map();
        for (var _b = 0, prev_1 = prev; _b < prev_1.length; _b++) {
            var item = prev_1[_b];
            if (item.data.type !== 'linkingCubes')
                continue;
            var cd = item.data;
            if (!cd.chainId || !affectedChainIds.has(cd.chainId))
                continue;
            if (!chainMembers.has(cd.chainId))
                chainMembers.set(cd.chainId, []);
            chainMembers.get(cd.chainId).push({ idx: (_a = cd.chainIndex) !== null && _a !== void 0 ? _a : 0, item: item });
        }
        // Build a map of item-id → new data overrides
        var overrides = new Map();
        chainMembers.forEach(function (members, _cId) {
            // Sort by chain index
            members.sort(function (a, b) { return a.idx - b.idx; });
            // Split into runs of "kept" cubes and mark removed cubes
            var currentRunChainId = null;
            var runIndex = 0;
            var chainOrientation = members[0].item.data.orientation;
            // UNLINK_SPACING increments only at group boundaries, not per-cube.
            // Cubes within the same sub-chain share the same offset so they stay connected.
            var cumulativeOffset = 0;
            var lastEntityType = null;
            members.forEach(function (_a) {
                var item = _a.item;
                var cubeId = item.data.id;
                var isSelected = selectedSet.has(cubeId);
                var oldCenter = item.data.center;
                if (isSelected) {
                    // Each selected cube is a separate entity → add spacing before it
                    if (lastEntityType !== null) {
                        cumulativeOffset += UNLINK_SPACING;
                    }
                    lastEntityType = 'individual';
                    var shiftedCenter = cumulativeOffset > 0 && oldCenter
                        ? chainOrientation === 'horizontal'
                            ? { x: oldCenter.x + cumulativeOffset, y: oldCenter.y }
                            : { x: oldCenter.x, y: oldCenter.y - cumulativeOffset }
                        : undefined;
                    // This cube is being unlinked → becomes individual
                    overrides.set(cubeId, {
                        chainId: undefined,
                        chainIndex: undefined,
                        center: shiftedCenter,
                    });
                    // Next kept cube starts a new sub-chain
                    currentRunChainId = null;
                    runIndex = 0;
                }
                else {
                    // This cube stays linked — assign it to the current sub-chain
                    if (!currentRunChainId) {
                        // Starting a new sub-chain → add spacing before it
                        if (lastEntityType !== null) {
                            cumulativeOffset += UNLINK_SPACING;
                        }
                        currentRunChainId = "chain_".concat(uuidv4().replace(/-/g, ''));
                        runIndex = 0;
                        lastEntityType = 'chain';
                    }
                    var shiftedCenter = cumulativeOffset > 0 && oldCenter
                        ? chainOrientation === 'horizontal'
                            ? { x: oldCenter.x + cumulativeOffset, y: oldCenter.y }
                            : { x: oldCenter.x, y: oldCenter.y - cumulativeOffset }
                        : undefined;
                    overrides.set(cubeId, {
                        chainId: currentRunChainId,
                        chainIndex: runIndex,
                        center: shiftedCenter,
                    });
                    runIndex++;
                }
            });
            // If a sub-chain ended up with only 1 cube, make it individual
            var subChainCounts = new Map();
            overrides.forEach(function (ov, itemId) {
                if (ov.chainId) {
                    if (!subChainCounts.has(ov.chainId))
                        subChainCounts.set(ov.chainId, []);
                    subChainCounts.get(ov.chainId).push(itemId);
                }
            });
            subChainCounts.forEach(function (ids, _scId) {
                if (ids.length === 1) {
                    var existing = overrides.get(ids[0]);
                    overrides.set(ids[0], __assign(__assign({}, existing), { chainId: undefined, chainIndex: undefined }));
                }
            });
        });
        return prev.map(function (item) {
            if (!item.data.id)
                return item;
            var ov = overrides.get(item.data.id);
            if (!ov)
                return item;
            oldItems.push(item);
            var newItem = __assign(__assign({}, item), { data: __assign(__assign(__assign({}, item.data), { chainId: ov.chainId, chainIndex: ov.chainIndex }), (ov.center ? { center: ov.center } : {})) });
            newItems.push(newItem);
            return newItem;
        });
    });
    saveState({
        type: 'multipleManipulatives',
        id: Array.from(affectedChainIds).join(','),
        propertyName: 'multipleCompleteData',
        old: JSON.stringify(oldItems),
        new: JSON.stringify(newItems),
    });
    return true;
};
